// src/components/POSView.js

import React, { useEffect, useRef, useState } from "react";
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
} from "lucide-react";

import {
  getAvailableMenuItems,
  getCategories,
  getBusinessProfile,
  getBusinessLogoBlob,
  getOrderItems,
  addOrderItem,
  updateOrderItem,
  deleteOrderItem,
  updateOrderType,
  updateOrderNotes,
  checkoutOrder,
} from "../api";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8080";

// =========================================================
// DEFAULT "NO IMAGE" ILLUSTRATION — plate & spoon, SwiftOrder style
// =========================================================

const PLATE_SPOON_SVG_MARKUP = `
  <div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f2ede1;">
    <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="27" fill="#fbf9f4" stroke="#ddd4c2" stroke-width="2"/>
      <circle cx="32" cy="32" r="17" fill="none" stroke="#ddd4c2" stroke-width="2"/>
      <path d="M17 16 L17 30 C17 32.2 18.8 34 21 34 C23.2 34 25 32.2 25 30 L25 16" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
      <line x1="21" y1="16" x2="21" y2="48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
      <path d="M45 16 C41 16 39 20 39 25 C39 29 41 31 45 31 L45 48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round" fill="none"/>
    </svg>
  </div>
`;

export default function POSView({
  table,
  order,
  onOrderCompleted,
}) {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] =
    useState("all");

  const [cartItems, setCartItems] = useState([]);
  const [addingItemId, setAddingItemId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cartDataStale, setCartDataStale] = useState(false);

  const [orderType, setOrderType] = useState(
    order?.orderType || "DINE_IN"
  );

  const [specialInstructions, setSpecialInstructions] =
    useState(order?.specialInstructions || "");

  const [discountPercent, setDiscountPercent] =
    useState(Number(order?.discountPercent || 0));

  const [discountAmountInput, setDiscountAmountInput] =
    useState(Number(order?.discountAmount || 0));

  const [cashAmount, setCashAmount] =
    useState(Number(order?.cashAmount || 0));

  const [onlineAmount, setOnlineAmount] =
    useState(Number(order?.onlineAmount || 0));

  const [businessProfile, setBusinessProfile] =
    useState(null);

  const [logoUrl, setLogoUrl] = useState(null);

  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  const [showCartDrawer, setShowCartDrawer] =
    useState(false);

  const receiptRef = useRef(null);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < 768
      : false
  );

  // =========================================================
  // MOBILE
  // =========================================================

  useEffect(() => {
    const resize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  // =========================================================
  // ORDER STATUS
  // =========================================================

  const orderStatus = String(
    order?.status || ""
  ).toUpperCase();

  const isEditable =
    !order ||
    orderStatus === "DRAFT";

  const isSentToKitchen =
    orderStatus === "PENDING" ||
    orderStatus === "IN_PROGRESS";

  const isKitchenReady =
    orderStatus === "READY";

  // =========================================================
  // SYNC ORDER
  // =========================================================

  useEffect(() => {
    setOrderType(
      order?.orderType || "DINE_IN"
    );

    setSpecialInstructions(
      order?.specialInstructions || ""
    );

    setDiscountPercent(
      Number(order?.discountPercent || 0)
    );

    setDiscountAmountInput(
      Number(order?.discountAmount || 0)
    );

    setCashAmount(Number(order?.cashAmount || 0));
    setOnlineAmount(Number(order?.onlineAmount || 0));
  }, [order?.id]);

  // =========================================================
  // LOAD
  // =========================================================

  useEffect(() => {
    loadMenuItems();
    loadCategories();
    loadBusinessProfile();
  }, []);

  useEffect(() => {
    if (order?.id) {
      loadOrderItems();
    } else {
      setCartItems([]);
    }
  }, [
    order?.id,
    order?.status,
  ]);

  // =========================================================
  // LOAD MENU
  // =========================================================

  const loadMenuItems = async () => {
    try {
      const data =
        await getAvailableMenuItems();

      setMenuItems(data || []);
    } catch (error) {
      console.error(
        "Failed to load menu items:",
        error
      );
    }
  };

  // =========================================================
  // LOAD CATEGORIES
  // =========================================================

  const loadCategories = async () => {
    try {
      const data =
        await getCategories();

      setCategories(data || []);
    } catch (error) {
      console.error(
        "Failed to load categories:",
        error
      );
    }
  };

  // =========================================================
  // LOAD BUSINESS PROFILE
  // =========================================================

  const loadBusinessProfile = async () => {
    try {
      const data =
        await getBusinessProfile();

      if (!data) {
        return;
      }

      setBusinessProfile(data);

      if (data.hasLogo) {
        try {
          const response =
            await getBusinessLogoBlob();

          const url =
            URL.createObjectURL(
              response.data
            );

          setLogoUrl(url);
        } catch (error) {
          console.error(
            "Logo loading failed:",
            error
          );
        }
      }
    } catch (error) {
      console.error(
        "Business profile error:",
        error
      );
    }
  };

  // =========================================================
  // LOAD ORDER ITEMS
  // =========================================================

  const loadOrderItems = async () => {
    if (!order?.id) {
      return;
    }

    try {
      const data =
        await getOrderItems(order.id);

      setCartItems(data || []);
      setCartDataStale(false);
    } catch (error) {
      console.error(
        "Failed to load order items:",
        error
      );

      setCartDataStale(true);
    }
  };

  // =========================================================
  // HELPERS
  // =========================================================

  const toNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : 0;
  };

  const roundMoney = (value) => {
    return Math.round(
      (toNumber(value) + Number.EPSILON) * 100
    ) / 100;
  };

  const formatCurrency = (value) => {
    return `₹${toNumber(value).toFixed(2)}`;
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "";
    }

    const date = new Date(value);

    return (
      `${date.toLocaleDateString()} ` +
      `${date.toLocaleTimeString()}`
    );
  };

  const formatOrderType = (type) => {
    return type === "TAKEAWAY"
      ? "🥡 Take Away"
      : "🍽 Dine In";
  };

  // =========================================================
  // GST
  // =========================================================

  const isGstEnabled =
    businessProfile?.gstEnabled === true;

  const isTaxInclusive =
    businessProfile?.taxInclusive === true;

  const businessDefaultTaxRate =
    toNumber(
      businessProfile?.defaultTaxRate
    );

  /*
   * TAX PRIORITY
   *
   * 1. OrderItem.taxRate
   * 2. MenuItem.taxRate
   * 3. Business defaultTaxRate
   *
   * Zero means use business default.
   */

  const getTaxRate = (item) => {
    const orderItemRate =
      toNumber(item?.taxRate);

    if (orderItemRate > 0) {
      return orderItemRate;
    }

    const menuItemRate =
      toNumber(
        item?.menuItem?.taxRate
      );

    if (menuItemRate > 0) {
      return menuItemRate;
    }

    return businessDefaultTaxRate;
  };

  // =========================================================
  // ITEM TAX
  // =========================================================

  const calculateItemTax = (item) => {
    if (!isGstEnabled) {
      return 0;
    }

    const rate =
      getTaxRate(item);

    if (rate <= 0) {
      return 0;
    }

    const lineTotal =
      toNumber(item?.lineTotal);

    /*
     * If backend already calculated tax,
     * use that value.
     */
    if (
      item?.taxAmount !== null &&
      item?.taxAmount !== undefined &&
      toNumber(item.taxAmount) > 0
    ) {
      return roundMoney(
        item.taxAmount
      );
    }

    /*
     * GST INCLUDED
     *
     * Example:
     * Price = ₹525
     * GST = 5%
     *
     * Base = 525 / 1.05 = 500
     * GST = 25
     */
    if (isTaxInclusive) {
      const base =
        lineTotal /
        (1 + rate / 100);

      return roundMoney(
        lineTotal - base
      );
    }

    /*
     * GST EXTRA
     *
     * Example:
     * Price = ₹500
     * GST = 5%
     * GST = ₹25
     * Total = ₹525
     */
    const baseAmount = roundMoney(
      toNumber(item?.price) * Math.max(0, toNumber(item?.quantity))
    );

    return roundMoney(baseAmount * rate / 100);
  };

  // =========================================================
  // ITEM BASE
  // =========================================================

  const calculateItemBaseAmount = (item) => {
    const tax = calculateItemTax(item);

    if (isGstEnabled && isTaxInclusive) {
      return roundMoney(toNumber(item?.lineTotal) - tax);
    }

    return roundMoney(
      toNumber(item?.price) * Math.max(0, toNumber(item?.quantity))
    );
  };

  // =========================================================
  // CART TOTALS
  // =========================================================

  const calculateCartTotals = () => {
    let subtotal = 0;
    let taxAmount = 0;
    let grossTotal = 0;

    cartItems.forEach((item) => {
      const lineTotal =
        toNumber(item?.lineTotal);

      const tax =
        calculateItemTax(item);

      if (
        isGstEnabled &&
        isTaxInclusive
      ) {
        /*
         * lineTotal already contains GST.
         */
        subtotal +=
          lineTotal - tax;

        taxAmount += tax;

        grossTotal +=
          lineTotal;
      } else {
        /*
         * OrderItemService stores the original unit price in price
         * and stores the GST-inclusive line amount in lineTotal.
         * Calculate from price × quantity so GST is not counted twice.
         */
        const itemBase = roundMoney(
          toNumber(item?.price) * Math.max(0, toNumber(item?.quantity))
        );

        subtotal += itemBase;
        taxAmount += tax;
        grossTotal += itemBase + tax;
      }
    });

    subtotal =
      roundMoney(subtotal);

    taxAmount =
      roundMoney(taxAmount);

    grossTotal =
      roundMoney(grossTotal);

    let percent =
      toNumber(discountPercent);

    let amount =
      toNumber(discountAmountInput);

    if (percent < 0) {
      percent = 0;
    }

    if (percent > 100) {
      percent = 100;
    }

    if (amount < 0) {
      amount = 0;
    }

    if (percent > 0) {
      amount =
        roundMoney(
          grossTotal *
          percent /
          100
        );
    }

    amount =
      Math.min(
        amount,
        grossTotal
      );

    const totalAmount =
      roundMoney(
        Math.max(
          0,
          grossTotal - amount
        )
      );

    return {
      subtotal,
      taxAmount,
      grossTotal,
      discountPercent:
        roundMoney(percent),
      discountAmount:
        amount,
      totalAmount,
    };
  };

  const cartTotals =
    calculateCartTotals();

  // =========================================================
  // FILTER
  // =========================================================

  const filteredItems =
    selectedCategory === "all"
      ? menuItems
      : menuItems.filter(
          (item) =>
            item.category &&
            item.category.id ===
              selectedCategory
        );

  // =========================================================
  // ADD ITEM
  // =========================================================

  const addToCart = async (menuItem) => {
    if (!order?.id) {
      alert(
        "No active order found."
      );
      return;
    }

    if (!isEditable) {
      alert(
        "This order has been sent to the kitchen. Editing is disabled."
      );
      return;
    }

    if (
      addingItemId ===
      menuItem.id
    ) {
      return;
    }

    setAddingItemId(
      menuItem.id
    );

    try {
      await addOrderItem(
        order.id,
        menuItem.id
      );

      await loadOrderItems();

      if (isMobile) {
        setShowCartDrawer(true);
      }
    } catch (error) {
      console.error(error);

      alert(
        "Unable to add item."
      );
    } finally {
      setAddingItemId(null);
    }
  };

  // =========================================================
  // QUANTITY
  // =========================================================

  const updateQuantity = async (
    orderItemId,
    change
  ) => {
    if (!isEditable) {
      return;
    }

    try {
      const item =
        cartItems.find(
          (current) =>
            current.id ===
            orderItemId
        );

      if (!item) {
        return;
      }

      const newQuantity =
        item.quantity +
        change;

      if (newQuantity <= 0) {
        await deleteOrderItem(
          orderItemId
        );
      } else {
        await updateOrderItem(
          orderItemId,
          newQuantity
        );
      }

      await loadOrderItems();
    } catch (error) {
      console.error(error);

      alert(
        "Unable to update quantity."
      );
    }
  };

  // =========================================================
  // REMOVE
  // =========================================================

  const removeFromCart = async (
    orderItemId
  ) => {
    if (!isEditable) {
      return;
    }

    try {
      await deleteOrderItem(
        orderItemId
      );

      await loadOrderItems();
    } catch (error) {
      console.error(error);

      alert(
        "Unable to remove item."
      );
    }
  };

  // =========================================================
  // ORDER TYPE
  // =========================================================

  const handleOrderTypeChange =
    async (type) => {
      if (!isEditable) {
        return;
      }

      setOrderType(type);

      try {
        await updateOrderType(
          order.id,
          type
        );
      } catch (error) {
        console.error(error);

        alert(
          "Unable to save order type."
        );
      }
    };

  // =========================================================
  // NOTES
  // =========================================================

  const handleNotesBlur =
    async () => {
      if (
        !order?.id ||
        !isEditable
      ) {
        return;
      }

      try {
        await updateOrderNotes(
          order.id,
          specialInstructions
        );
      } catch (error) {
        console.error(error);

        alert(
          "Unable to save notes."
        );
      }
    };

  // =========================================================
  // COMPLETE ORDER / INVOICE
  // =========================================================

  const handleCompleteOrder =
    () => {
      if (!order?.id) {
        alert(
          "No active order."
        );
        return;
      }

      if (
        cartItems.length === 0
      ) {
        alert(
          "Cart is empty."
        );
        return;
      }

      const invoiceItems =
        cartItems.map(
          (item) => {
            const taxRate =
              getTaxRate(item);

            const taxAmount =
              calculateItemTax(item);

            const baseAmount =
              calculateItemBaseAmount(
                item
              );

            return {
              id: item.id,
              name:
                item.menuItem?.name ||
                "Item",
              quantity:
                item.quantity,
              price:
                toNumber(item.price),
              lineTotal:
                baseAmount,
              baseAmount,
              taxRate,
              taxAmount,
              specialInstructions:
                item.specialInstructions ||
                "",
            };
          }
        );

      setInvoiceData({
        orderNumber:
          order.orderNumber,

        orderType,

        specialInstructions,

        createdAt:
          order.createdAt ||
          new Date().toISOString(),

        subtotal:
          cartTotals.subtotal,

        taxAmount:
          cartTotals.taxAmount,

        grossTotal:
          cartTotals.grossTotal,

        discountPercent:
          cartTotals.discountPercent,

        discountAmount:
          cartTotals.discountAmount,

        totalAmount:
          cartTotals.totalAmount,

        gstEnabled:
          isGstEnabled,

        taxInclusive:
          isTaxInclusive,

        items:
          invoiceItems,
      });

      setShowInvoice(true);
    };

  // =========================================================
  // DONE / SEND KITCHEN
  // =========================================================

  const handleInvoiceDone =
    async () => {
      if (!order?.id) {
        setShowInvoice(false);
        return;
      }

      const finalTotal = roundMoney(invoiceData?.totalAmount);
      const cash = roundMoney(cashAmount);
      const online = roundMoney(onlineAmount);
      const paid = roundMoney(cash + online);

      if (cash < 0 || online < 0) {
        alert("Cash and online amounts cannot be negative.");
        return;
      }

      if (paid > finalTotal) {
        alert("Cash + online payment cannot exceed the final bill amount.");
        return;
      }

      setIsProcessing(true);

      try {
        const savedOrder = await checkoutOrder(order.id, {
          orderType,
          orderChannel: order.orderChannel || "POS",
          specialInstructions: specialInstructions || null,
          discountPercent: roundMoney(invoiceData.discountPercent),
          discountAmount: roundMoney(invoiceData.discountAmount),
          cashAmount: cash,
          onlineAmount: online,
        });

        if (typeof onOrderCompleted === "function") {
          onOrderCompleted(savedOrder || {
            ...order,
            orderType,
            orderChannel: order.orderChannel || "POS",
            specialInstructions,
            discountPercent: invoiceData.discountPercent,
            discountAmount: invoiceData.discountAmount,
            subtotal: invoiceData.subtotal,
            taxAmount: invoiceData.taxAmount,
            totalAmount: invoiceData.totalAmount,
            cashAmount: cash,
            onlineAmount: online,
          });
        }

        setCartItems([]);
        setSpecialInstructions("");
        setOrderType("DINE_IN");
        setDiscountPercent(0);
        setDiscountAmountInput(0);
        setCashAmount(0);
        setOnlineAmount(0);
        setInvoiceData(null);
        setShowInvoice(false);

        if (isMobile) {
          setShowCartDrawer(false);
        }
      } catch (error) {
        console.error("Normal POS checkout failed:", error);
        console.error("Server response:", error?.response?.data);

        const message =
          typeof error?.response?.data === "string"
            ? error.response.data
            : error?.response?.data?.message ||
              "Unable to save payment and send order to kitchen.";

        alert(message);
      } finally {
        setIsProcessing(false);
      }
    };

  // =========================================================
  // PRINT
  // =========================================================

  const handlePrint = () => {
    if (!receiptRef.current) {
      return;
    }

    const content =
      receiptRef.current.innerHTML;

    const printWindow =
      window.open(
        "",
        "_blank",
        "width=400,height=700"
      );

    if (!printWindow) {
      alert(
        "Popup blocked. Allow popups to print invoice."
      );
      return;
    }

    printWindow.document.open();

    printWindow.document.write(`
      <html>
        <head>
          <title>GST Invoice</title>

          <meta
            name="viewport"
            content="width=device-width, initial-scale=1"
          />

          <style>
            body {
              font-family:
                Arial,
                sans-serif;
              padding: 16px;
              background: white;
              color: #111827;
            }

            .line {
              display: flex;
              justify-content: space-between;
              margin: 4px 0;
            }

            .center {
              text-align: center;
            }

            .border-top {
              border-top:
                1px dashed #000;
              margin-top: 8px;
              padding-top: 8px;
            }

            img {
              max-width: 100%;
              height: auto;
            }
          </style>
        </head>

        <body>
          ${content}
        </body>
      </html>
    `);

    printWindow.document.close();

    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  // =========================================================
  // PDF
  // =========================================================

  const handleDownloadPDF =
    async () => {
      if (
        !receiptRef.current ||
        !invoiceData
      ) {
        return;
      }

      const element =
        receiptRef.current;

      const originalWidth =
        element.style.width;

      const originalMaxHeight =
        element.style.maxHeight;

      try {
        element.style.width =
          window.innerWidth > 900
            ? "700px"
            : `${Math.floor(
                window.innerWidth * 0.92
              )}px`;

        element.style.maxHeight =
          "none";

        const canvas =
          await html2canvas(
            element,
            {
              scale:
                Math.min(
                  2,
                  window.devicePixelRatio ||
                    1.5
                ) * 1.2,

              useCORS: true,

              scrollX: 0,

              scrollY:
                -window.scrollY,

              windowWidth:
                element.scrollWidth,

              windowHeight:
                element.scrollHeight,
            }
          );

        const imgData =
          canvas.toDataURL(
            "image/png"
          );

        const pdf =
          new jsPDF(
            "p",
            "pt",
            "a4"
          );

        const pageWidth =
          pdf.internal.pageSize.getWidth();

        const pageHeight =
          pdf.internal.pageSize.getHeight();

        const margin = 20;

        const imgWidth =
          pageWidth -
          margin * 2;

        const imgHeight =
          (canvas.height *
            imgWidth) /
          canvas.width;

        let heightLeft =
          imgHeight;

        let position =
          margin;

        pdf.addImage(
          imgData,
          "PNG",
          margin,
          position,
          imgWidth,
          imgHeight
        );

        heightLeft -=
          pageHeight -
          margin * 2;

        while (
          heightLeft > 0
        ) {
          pdf.addPage();

          position =
            margin -
            (
              imgHeight -
              heightLeft
            );

          pdf.addImage(
            imgData,
            "PNG",
            margin,
            position,
            imgWidth,
            imgHeight
          );

          heightLeft -=
            pageHeight -
            margin * 2;
        }

        pdf.save(
          `invoice_${
            invoiceData.orderNumber ||
            Date.now()
          }.pdf`
        );
      } catch (error) {
        console.error(
          "PDF generation failed:",
          error
        );

        alert(
          "Failed to generate PDF."
        );
      } finally {
        element.style.width =
          originalWidth || "";

        element.style.maxHeight =
          originalMaxHeight || "";
      }
    };

  // =========================================================
  // ORDER BUTTON
  // =========================================================

  const renderOrderActionButton =
    ({ mobile = false } = {}) => {
      if (!order) {
        return null;
      }

      const baseClass =
        mobile
          ? "flex-1 rounded-lg py-2 text-sm font-semibold"
          : "w-full py-3 rounded-lg font-semibold";

      if (
        orderStatus === "DRAFT"
      ) {
        return (
          <button
            type="button"
            onClick={
              handleCompleteOrder
            }
            disabled={
              cartItems.length === 0 ||
              isProcessing
            }
            className={`${baseClass} so-btn-primary`}
          >
            🧾 Complete Order
          </button>
        );
      }

      if (
        orderStatus === "PENDING" ||
        orderStatus === "IN_PROGRESS"
      ) {
        return (
          <button
            type="button"
            disabled
            className={`${baseClass} so-btn-waiting`}
          >
            ⏳ Waiting For Kitchen
          </button>
        );
      }

      if (
        orderStatus === "READY"
      ) {
        return (
          <button
            type="button"
            disabled
            className={`${baseClass} so-btn-ready`}
          >
            🍽 Kitchen Finished
          </button>
        );
      }

      return null;
    };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      {/* Scoped SwiftOrder theme — same tokens as Login.js,
          Dashboard.js, Sidebar.js and DirectPOSView.js. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-pos {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --herb-dark: #2d4f39;
          --mustard: #f2a93b;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          background: var(--paper-dim);
          font-family: var(--font-body);
          color: var(--ink);
        }

        .so-pos * {
          box-sizing: border-box;
        }

        .so-text-ink { color: var(--ink); }
        .so-text-stone { color: var(--stone); }
        .so-text-stone-light { color: var(--stone-light); }
        .so-text-herb { color: var(--herb-dark); }

        .so-page-title {
          font-family: var(--font-display);
          font-size: 1.9rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-subtitle {
          font-size: 0.85rem;
          color: var(--stone);
        }

        .so-cart-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--chili);
          color: var(--paper);
          padding: 10px 14px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.88rem;
          box-shadow: 0 8px 18px -8px rgba(228, 87, 46, 0.55);
        }

        .so-status-strip {
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 16px;
          font-size: 0.88rem;
          border: 1px solid var(--line);
        }

        .so-status-gst-on {
          background: #eaf3ee;
          border-color: var(--herb);
          color: var(--herb-dark);
        }

        .so-status-gst-off {
          background: var(--paper);
          color: var(--stone);
        }

        .so-status-stale {
          background: #fdf3e2;
          border: 1px solid var(--mustard);
          color: #854f0b;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 16px;
          display: flex;
          justify-content: space-between;
        }

        .so-status-sent {
          background: var(--paper-dim);
          border: 1px solid var(--line);
          color: var(--stone);
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 16px;
        }

        .so-status-ready {
          background: #eaf3ee;
          border: 1px solid var(--herb);
          color: var(--herb-dark);
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 16px;
        }

        .so-table-banner {
          background: #fdece6;
          border: 1px solid var(--chili);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .so-table-badge {
          background: var(--chili);
          color: var(--paper);
          padding: 8px 16px;
          border-radius: 999px;
          font-weight: 700;
          font-family: var(--font-mono);
          font-size: 0.78rem;
        }

        .so-type-toggle {
          padding: 9px 18px;
          border-radius: 8px;
          border: 1.5px solid var(--line);
          background: #fff;
          color: var(--stone);
          font-weight: 600;
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }

        .so-type-toggle:hover:not(:disabled) {
          border-color: var(--chili);
        }

        .so-type-toggle-active {
          background: var(--chili);
          color: var(--paper);
          border-color: var(--chili);
        }

        .so-category-pill {
          padding: 8px 14px;
          border-radius: 999px;
          white-space: nowrap;
          font-size: 0.85rem;
          font-weight: 600;
          background: #fff;
          color: var(--stone);
          border: 1px solid var(--line);
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }

        .so-category-pill:hover {
          border-color: var(--chili);
        }

        .so-category-pill-active {
          background: var(--ink);
          color: var(--paper);
          border-color: var(--ink);
        }

        .so-item-card {
          background: #fffdf8;
          border-radius: 12px;
          border: 1px solid var(--line);
          padding: 12px;
          text-align: left;
          transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
        }

        .so-item-card:hover:not(:disabled) {
          border-color: var(--chili);
          transform: translateY(-2px);
          box-shadow: 0 14px 24px -18px rgba(28, 26, 23, 0.4);
        }

        .so-item-card:disabled {
          opacity: 0.5;
        }

        .so-item-image-wrap {
          background: var(--paper-dim);
          border-radius: 8px;
        }

        .so-no-image {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--paper-dim);
        }

        .so-item-price {
          font-weight: 700;
          color: var(--herb-dark);
        }

        .so-item-gst {
          font-size: 0.7rem;
          color: var(--stone-light);
        }

        .so-cart-aside {
          background: #fffdf8;
          box-shadow: -8px 0 24px -20px rgba(28, 26, 23, 0.4);
          border-left: 1px solid var(--line);
        }

        .so-cart-empty {
          color: var(--stone-light);
        }

        .so-cart-line {
          background: var(--paper-dim);
        }

        .so-remove-btn {
          color: var(--chili);
        }

        .so-remove-btn:disabled {
          color: var(--stone-light);
        }

        .so-qty-btn {
          width: 32px;
          height: 32px;
          background: #fff;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--line);
          color: var(--ink);
        }

        .so-qty-btn:hover:not(:disabled) {
          background: var(--paper-dim);
        }

        .so-qty-btn:disabled {
          opacity: 0.4;
        }

        .so-textarea, .so-text-input {
          border: 1px solid var(--line);
          background: var(--paper);
          color: var(--ink);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .so-textarea:focus, .so-text-input:focus {
          border-color: var(--chili);
          box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
        }

        .so-textarea:disabled {
          background: var(--paper-dim);
        }

        .so-dashed-divider {
          border-top: 1px dashed var(--line);
        }

        .so-solid-divider {
          border-top: 1px solid var(--line);
        }

        .so-discount-panel {
          background: var(--paper-dim);
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 12px;
        }

        .so-btn-primary {
          background: var(--chili);
          color: var(--paper);
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
          transition: background 0.15s ease, transform 0.1s ease, opacity 0.15s ease;
        }

        .so-btn-primary:hover:not(:disabled) {
          background: var(--chili-dark);
        }

        .so-btn-primary:disabled {
          background: var(--stone-light);
          cursor: not-allowed;
          box-shadow: none;
        }

        .so-btn-waiting {
          background: var(--mustard);
          color: #412402;
          cursor: not-allowed;
        }

        .so-btn-ready {
          background: var(--herb);
          color: var(--paper);
          cursor: not-allowed;
        }

        .so-mobile-bar {
          background: #fffdf8;
          border-top: 1px solid var(--line);
        }

        .so-mobile-cart-chip {
          background: var(--paper-dim);
          color: var(--ink);
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .so-drawer-overlay {
          background: rgba(28, 26, 23, 0.5);
        }

        .so-drawer {
          background: #fffdf8;
        }

        .so-invoice-overlay {
          background: rgba(28, 26, 23, 0.6);
        }

        .so-invoice-card {
          background: #fffdf8;
          border-radius: 12px;
        }

        .so-invoice-title {
          font-family: var(--font-display);
          font-size: 1.3rem;
          color: var(--ink);
        }

        .so-invoice-btn-outline {
          border: 1px solid var(--line);
          color: var(--ink);
          background: #fff;
          transition: background 0.15s ease;
        }

        .so-invoice-btn-outline:hover:not(:disabled) {
          background: var(--paper-dim);
        }

        .so-invoice-btn-print {
          background: var(--herb);
          color: var(--paper);
        }

        .so-invoice-btn-print:hover:not(:disabled) {
          background: var(--herb-dark);
        }

        .so-invoice-btn-pdf {
          background: var(--ink);
          color: var(--paper);
        }

        .so-invoice-btn-pdf:hover:not(:disabled) {
          background: #302c27;
        }
      `}</style>

      <div className="so-pos flex flex-col md:flex-row h-full">

        {/* ===================================================
            LEFT
        =================================================== */}

        <div className="flex-1 p-4 md:p-6 overflow-auto">

          {/* HEADER */}

          <div className="flex items-center justify-between mb-4">

            <div>
              <h2 className="so-page-title">
                Point of Sale
              </h2>

              <p className="so-subtitle mt-1">
                Restaurant POS
              </p>
            </div>

            {isMobile && (
              <button
                type="button"
                onClick={() =>
                  setShowCartDrawer(true)
                }
                className="so-cart-toggle"
              >
                <ShoppingCart
                  size={18}
                />

                <span>
                  Cart ({cartItems.length})
                </span>
              </button>
            )}
          </div>

          {/* GST STATUS */}

          {businessProfile && (
            <div
              className={`so-status-strip ${
                isGstEnabled &&
                businessDefaultTaxRate > 0
                  ? "so-status-gst-on"
                  : "so-status-gst-off"
              }`}
            >
              {isGstEnabled &&
              businessDefaultTaxRate > 0 ? (
                <>
                  <strong>
                    GST Enabled
                  </strong>

                  {" • "}

                  Default GST:{" "}
                  <strong>
                    {businessDefaultTaxRate}%
                  </strong>

                  {isTaxInclusive && (
                    <>
                      {" • "}
                      GST included in prices
                    </>
                  )}
                </>
              ) : (
                "GST Disabled"
              )}
            </div>
          )}

          {/* STALE */}

          {cartDataStale && (
            <div className="so-status-stale">
              <span>
                Cart data may be out of date.
              </span>

              <button
                type="button"
                onClick={
                  loadOrderItems
                }
                className="underline font-semibold"
              >
                Retry
              </button>
            </div>
          )}

          {/* KITCHEN STATUS */}

          {isSentToKitchen && (
            <div className="so-status-sent">
              This order has been sent to the kitchen.
              Editing is disabled until the kitchen finishes.
            </div>
          )}

          {isKitchenReady && (
            <div className="so-status-ready">
              Kitchen has finished this order.
              It is ready for billing.
            </div>
          )}

          {/* TABLE */}

          {table && (
            <div className="so-table-banner">

              <div className="flex justify-between items-center">

                <div>

                  <h3 className="text-xl font-bold" style={{ color: "var(--chili-dark)" }}>
                    🍽 {table.tableName}
                  </h3>

                  <p className="mt-1" style={{ color: "var(--chili-dark)" }}>
                    Capacity: {table.capacity}
                  </p>

                  <p style={{ color: "var(--chili-dark)" }}>
                    Order:{" "}
                    {order?.orderNumber}
                  </p>

                </div>

                <div className="so-table-badge">
                  {table.status}
                </div>

              </div>
            </div>
          )}

          {/* ORDER TYPE */}

          {order && (
            <div className="mb-5">

              <p className="text-sm font-semibold so-text-ink mb-2">
                Order Type
              </p>

              <div className="flex gap-3">

                <button
                  type="button"
                  onClick={() =>
                    handleOrderTypeChange(
                      "DINE_IN"
                    )
                  }
                  disabled={!isEditable}
                  className={`so-type-toggle ${
                    orderType === "DINE_IN"
                      ? "so-type-toggle-active"
                      : ""
                  }`}
                >
                  🍽 Dine In
                </button>

                <button
                  type="button"
                  onClick={() =>
                    handleOrderTypeChange(
                      "TAKEAWAY"
                    )
                  }
                  disabled={!isEditable}
                  className={`so-type-toggle ${
                    orderType === "TAKEAWAY"
                      ? "so-type-toggle-active"
                      : ""
                  }`}
                >
                  🥡 Take Away
                </button>

              </div>
            </div>
          )}

          {/* CATEGORIES */}

          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">

            <button
              type="button"
              onClick={() =>
                setSelectedCategory(
                  "all"
                )
              }
              className={`so-category-pill ${
                selectedCategory === "all"
                  ? "so-category-pill-active"
                  : ""
              }`}
            >
              All Items
            </button>

            {categories.map(
              (category) => (
                <button
                  type="button"
                  key={category.id}
                  onClick={() =>
                    setSelectedCategory(
                      category.id
                    )
                  }
                  className={`so-category-pill ${
                    selectedCategory === category.id
                      ? "so-category-pill-active"
                      : ""
                  }`}
                >
                  {category.name}
                </button>
              )
            )}

          </div>

          {/* MENU */}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">

            {filteredItems.map(
              (item) => {

                const adding =
                  addingItemId ===
                  item.id;

                const itemTaxRate =
                  toNumber(
                    item.taxRate
                  ) > 0
                    ? toNumber(
                        item.taxRate
                      )
                    : businessDefaultTaxRate;

                const displayPrice =
                  toNumber(
                    item.price
                  );

                const gstAmount =
                  isGstEnabled &&
                  !isTaxInclusive
                    ? roundMoney(
                        displayPrice *
                        itemTaxRate /
                        100
                      )
                    : 0;

                const finalPrice =
                  isGstEnabled &&
                  !isTaxInclusive
                    ? roundMoney(
                        displayPrice +
                        gstAmount
                      )
                    : displayPrice;

                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() =>
                      addToCart(item)
                    }
                    disabled={
                      adding ||
                      !isEditable
                    }
                    className="so-item-card"
                  >

                    <div className="so-item-image-wrap h-28 md:h-32 mb-3 overflow-hidden flex items-center justify-center">

                      <img
                        src={`${API_BASE_URL}/api/menu-items/${item.id}/image`}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(event) => {

                          if (
                            item.imageUrl
                          ) {
                            event.currentTarget.src =
                              item.imageUrl;
                          } else {
                            event.currentTarget.style.display =
                              "none";

                            const parent =
                              event.currentTarget
                                .parentElement;

                            if (parent) {
                              parent.innerHTML =
                                PLATE_SPOON_SVG_MARKUP;
                            }
                          }

                        }}
                      />

                    </div>

                    <h3 className="font-semibold so-text-ink">
                      {item.name}
                    </h3>

                    <div className="mt-2">

                      <p className="text-lg so-item-price">
                        {formatCurrency(
                          finalPrice
                        )}
                      </p>

                      {isGstEnabled &&
                        itemTaxRate > 0 && (
                          <p className="so-item-gst mt-1">
                            GST {itemTaxRate}%
                            {!isTaxInclusive &&
                              ` • ${formatCurrency(
                                gstAmount
                              )}`}
                            {isTaxInclusive &&
                              " included"}
                          </p>
                        )}

                    </div>

                  </button>
                );
              }
            )}

          </div>
        </div>

        {/* ===================================================
            DESKTOP CART
        =================================================== */}

        <aside className="so-cart-aside hidden md:flex md:w-96 p-4 md:p-6 flex-col">

          <div className="flex items-center gap-2 mb-4">

            <ShoppingCart
              size={24}
              color="#1c1a17"
            />

            <h3 className="so-page-title" style={{ fontSize: "1.5rem" }}>
              Cart
            </h3>

          </div>

          <div className="flex-1 overflow-auto mb-4">

            {cartItems.length === 0 ? (
              <div className="so-cart-empty text-center mt-12">

                <ShoppingCart
                  size={48}
                  className="mx-auto mb-2 opacity-50"
                />

                <p>
                  Cart is empty
                </p>

              </div>
            ) : (
              <div className="space-y-3">

                {cartItems.map(
                  (item) => {

                    const taxRate =
                      getTaxRate(item);

                    const tax =
                      calculateItemTax(
                        item
                      );

                    const finalLineTotal =
                      toNumber(item.lineTotal) ||
                      roundMoney(
                        toNumber(item.price) * toNumber(item.quantity) + tax
                      );

                    return (
                      <div
                        key={item.id}
                        className="so-cart-line rounded-lg p-3"
                      >

                        <div className="flex justify-between items-start mb-2">

                          <div>

                            <h4 className="font-semibold so-text-ink">
                              {
                                item.menuItem
                                  ?.name
                              }
                            </h4>

                            {isGstEnabled &&
                              taxRate > 0 && (
                                <p className="text-xs so-text-stone">
                                  GST{" "}
                                  {taxRate}%
                                  {" • "}
                                  {formatCurrency(
                                    tax
                                  )}
                                </p>
                              )}

                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              removeFromCart(
                                item.id
                              )
                            }
                            disabled={
                              !isEditable
                            }
                            className="so-remove-btn"
                          >
                            <Trash2
                              size={16}
                            />
                          </button>

                        </div>

                        <div className="flex justify-between items-center">

                          <div className="flex items-center gap-2">

                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(
                                  item.id,
                                  -1
                                )
                              }
                              disabled={
                                !isEditable
                              }
                              className="so-qty-btn"
                            >
                              <Minus
                                size={16}
                              />
                            </button>

                            <span className="w-8 text-center font-medium so-text-ink">
                              {item.quantity}
                            </span>

                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(
                                  item.id,
                                  1
                                )
                              }
                              disabled={
                                !isEditable
                              }
                              className="so-qty-btn"
                            >
                              <Plus
                                size={16}
                              />
                            </button>

                          </div>

                          <span className="font-bold so-text-ink">
                            {formatCurrency(
                              finalLineTotal
                            )}
                          </span>

                        </div>

                      </div>
                    );
                  }
                )}

              </div>
            )}

          </div>

          {/* NOTES */}

          {order && (
            <div className="mb-4">

              <label className="text-sm font-semibold so-text-ink mb-1 block">
                Special Instructions
              </label>

              <textarea
                value={
                  specialInstructions
                }
                onChange={(event) =>
                  setSpecialInstructions(
                    event.target.value
                  )
                }
                onBlur={
                  handleNotesBlur
                }
                disabled={
                  !isEditable
                }
                rows={2}
                placeholder="Extra sauce, no onion, less spicy..."
                className="so-textarea w-full text-sm rounded-lg p-2"
              />

            </div>
          )}

          {/* TOTALS */}

          <div className="so-solid-divider pt-4">

            <div className="flex justify-between text-sm mb-2 so-text-ink">

              <span>
                Subtotal
              </span>

              <span className="font-medium">
                {formatCurrency(
                  cartTotals.subtotal
                )}
              </span>

            </div>

            {isGstEnabled && (
              <div className="flex justify-between text-sm mb-3 so-text-ink">

                <span>
                  GST
                </span>

                <span className="font-medium">
                  {formatCurrency(
                    cartTotals.taxAmount
                  )}
                </span>

              </div>
            )}

            {isEditable && (
              <div className="so-discount-panel mb-3">

                <div className="text-sm font-semibold so-text-ink mb-2">
                  Discount
                </div>

                <div className="grid grid-cols-2 gap-2">

                  <div>
                    <label className="text-xs font-medium so-text-stone mb-1 block">
                      Discount Percentage (%)
                    </label>

                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={
                        discountPercent
                      }
                      onChange={(event) => {
                        setDiscountPercent(
                          Math.min(
                            100,
                            Math.max(
                              0,
                              Number(
                                event.target.value
                              ) || 0
                            )
                          )
                        );
                      }}
                      placeholder="Discount %"
                      className="so-text-input w-full rounded-lg px-2 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium so-text-stone mb-1 block">
                      Fixed Amount (₹)
                    </label>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        discountPercent > 0
                          ? cartTotals.discountAmount
                          : discountAmountInput
                      }
                      onChange={(event) => {
                        setDiscountPercent(
                          0
                        );

                        setDiscountAmountInput(
                          Math.max(
                            0,
                            Number(
                              event.target.value
                            ) || 0
                          )
                        );
                      }}
                      placeholder="Amount ₹"
                      className="so-text-input w-full rounded-lg px-2 py-2 text-sm"
                    />
                  </div>

                </div>

              </div>
            )}

            {cartTotals.discountAmount >
              0 && (
              <div className="flex justify-between text-sm mb-3" style={{ color: "var(--herb-dark)" }}>

                <span>
                  Discount
                  {cartTotals.discountPercent >
                    0 &&
                    ` (${cartTotals.discountPercent}%)`}
                </span>

                <span>
                  -
                  {formatCurrency(
                    cartTotals.discountAmount
                  )}
                </span>

              </div>
            )}

            <div className="flex justify-between items-center mb-3">

              <span className="text-xl font-bold so-text-ink">
                Total
              </span>

              <span className="text-2xl font-bold so-text-herb">
                {formatCurrency(
                  cartTotals.totalAmount
                )}
              </span>

            </div>

            {renderOrderActionButton()}

          </div>

        </aside>

        {/* ===================================================
            MOBILE BAR
        =================================================== */}

        {isMobile && (
          <div className="so-mobile-bar fixed bottom-0 left-0 right-0 p-3 flex items-center gap-3 z-40">

            <div className="flex-1">

              <div className="text-sm so-text-stone">
                Total
              </div>

              <div className="text-lg font-bold so-text-herb">
                {formatCurrency(
                  cartTotals.totalAmount
                )}
              </div>

            </div>

            <button
              type="button"
              onClick={() =>
                setShowCartDrawer(true)
              }
              className="so-mobile-cart-chip"
            >
              Cart ({
                cartItems.length
              })
            </button>

            {renderOrderActionButton({
              mobile: true,
            })}

          </div>
        )}

      </div>

      {/* =====================================================
          MOBILE CART
      ===================================================== */}

      {isMobile &&
        showCartDrawer && (
          <div className="fixed inset-0 z-50 flex">

            <div
              className="absolute inset-0 so-drawer-overlay"
              onClick={() =>
                setShowCartDrawer(false)
              }
            />

            <div className="so-drawer relative w-full max-h-[90vh] rounded-t-xl shadow-xl mt-auto overflow-auto p-4">

              <div className="flex justify-between mb-4">

                <h3 className="text-lg font-semibold so-text-ink">
                  Cart
                </h3>

                <button
                  type="button"
                  onClick={() =>
                    setShowCartDrawer(false)
                  }
                  className="so-text-stone"
                >
                  Close
                </button>

              </div>

              {!isEditable && (
                <div className="so-status-sent">
                  This order has been sent to the kitchen.
                  Editing is disabled.
                </div>
              )}

              {cartItems.map(
                (item) => {

                  const taxRate =
                    getTaxRate(item);

                  const tax =
                    calculateItemTax(
                      item
                    );

                  const finalLineTotal =
                      toNumber(item.lineTotal) ||
                      roundMoney(
                        toNumber(item.price) * toNumber(item.quantity) + tax
                      );

                  return (
                    <div
                      key={item.id}
                      className="so-cart-line rounded-lg p-3 mb-3"
                    >

                      <div className="flex justify-between">

                        <div>

                          <h4 className="font-semibold so-text-ink">
                            {
                              item.menuItem
                                ?.name
                            }
                          </h4>

                          {isGstEnabled &&
                            taxRate > 0 && (
                              <p className="text-xs so-text-stone">
                                GST{" "}
                                {taxRate}%
                                {" • "}
                                {formatCurrency(
                                  tax
                                )}
                              </p>
                            )}

                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            removeFromCart(
                              item.id
                            )
                          }
                          disabled={
                            !isEditable
                          }
                          className="so-remove-btn"
                        >
                          <Trash2
                            size={16}
                          />
                        </button>

                      </div>

                      <div className="flex justify-between items-center mt-3">

                        <div className="flex items-center gap-2">

                          <button
                            type="button"
                            onClick={() =>
                              updateQuantity(
                                item.id,
                                -1
                              )
                            }
                            disabled={
                              !isEditable
                            }
                            className="so-qty-btn"
                          >
                            <Minus
                              size={16}
                              className="mx-auto"
                            />
                          </button>

                          <span className="so-text-ink">
                            {item.quantity}
                          </span>

                          <button
                            type="button"
                            onClick={() =>
                              updateQuantity(
                                item.id,
                                1
                              )
                            }
                            disabled={
                              !isEditable
                            }
                            className="so-qty-btn"
                          >
                            <Plus
                              size={16}
                              className="mx-auto"
                            />
                          </button>

                        </div>

                        <strong className="so-text-ink">
                          {formatCurrency(
                            finalLineTotal
                          )}
                        </strong>

                      </div>

                    </div>
                  );
                }
              )}

              {/* MOBILE NOTES */}

              {order && (
                <div className="mb-4">

                  <label className="text-sm font-semibold so-text-ink block mb-1">
                    Special Instructions
                  </label>

                  <textarea
                    value={
                      specialInstructions
                    }
                    onChange={(event) =>
                      setSpecialInstructions(
                        event.target.value
                      )
                    }
                    onBlur={
                      handleNotesBlur
                    }
                    disabled={
                      !isEditable
                    }
                    rows={2}
                    placeholder="Extra sauce, no onion..."
                    className="so-textarea w-full rounded-lg p-2 text-sm"
                  />

                </div>
              )}

              <div className="so-solid-divider pt-3">

                <div className="flex justify-between mb-2 so-text-ink">

                  <span>
                    Subtotal
                  </span>

                  <span>
                    {formatCurrency(
                      cartTotals.subtotal
                    )}
                  </span>

                </div>

                {isGstEnabled && (
                  <div className="flex justify-between mb-2 so-text-ink">

                    <span>
                      GST
                    </span>

                    <span>
                      {formatCurrency(
                        cartTotals.taxAmount
                      )}
                    </span>

                  </div>
                )}

                {cartTotals.discountAmount >
                  0 && (
                  <div className="flex justify-between mb-2" style={{ color: "var(--herb-dark)" }}>

                    <span>
                      Discount
                    </span>

                    <span>
                      -
                      {formatCurrency(
                        cartTotals.discountAmount
                      )}
                    </span>

                  </div>
                )}

                <div className="flex justify-between font-bold text-lg mb-3 so-text-ink">

                  <span>
                    Total
                  </span>

                  <span className="so-text-herb">
                    {formatCurrency(
                      cartTotals.totalAmount
                    )}
                  </span>

                </div>

                <div className="flex gap-3">

                  <button
                    type="button"
                    onClick={() =>
                      setShowCartDrawer(false)
                    }
                    className="so-invoice-btn-outline flex-1 rounded-lg py-2"
                  >
                    Continue
                  </button>

                  {renderOrderActionButton({
                    mobile: true,
                  })}

                </div>

              </div>

            </div>

          </div>
        )}

      {/* =====================================================
          INVOICE
      ===================================================== */}

      {showInvoice &&
        invoiceData && (
          <div className="fixed inset-0 so-invoice-overlay flex items-center justify-center z-50 px-4">

            <div className="so-invoice-card shadow-xl w-full max-w-md p-4 md:p-6 overflow-auto max-h-[95vh]">

              <div
                ref={receiptRef}
                style={{
                  background: "white",
                  paddingBottom: 40,
                }}
              >

                {/* BUSINESS */}

                <div className="text-center mb-3">

                  {logoUrl && (
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="h-12 mx-auto mb-2 object-contain"
                    />
                  )}

                  <h3 className="so-invoice-title">
                    {
                      businessProfile?.businessName ||
                      "Restaurant POS"
                    }
                  </h3>

                  {businessProfile?.addressLine1 && (
                    <p className="text-xs so-text-stone">
                      {
                        businessProfile.addressLine1
                      }
                    </p>
                  )}

                  {(businessProfile?.city ||
                    businessProfile?.state ||
                    businessProfile?.pinCode) && (
                    <p className="text-xs so-text-stone">
                      {[
                        businessProfile?.city,
                        businessProfile?.state,
                        businessProfile?.pinCode,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  )}

                  {businessProfile?.phone && (
                    <p className="text-xs so-text-stone">
                      Tel:{" "}
                      {
                        businessProfile.phone
                      }
                    </p>
                  )}

                  {invoiceData.gstEnabled &&
                    businessProfile?.gstin && (
                      <p className="text-xs font-semibold so-text-ink mt-1">
                        GSTIN:{" "}
                        {
                          businessProfile.gstin
                        }
                      </p>
                    )}

                </div>

                {/* ORDER */}

                <div className="flex justify-between text-xs mb-2 so-text-ink">

                  <span>
                    Order:{" "}
                    {
                      invoiceData.orderNumber
                    }
                    {" "}
                    (
                    {formatOrderType(
                      invoiceData.orderType
                    )}
                    )
                  </span>

                  <span>
                    {formatDateTime(
                      invoiceData.createdAt
                    )}
                  </span>

                </div>

                <div className="so-dashed-divider my-2" />

                {/* ITEMS */}

                <div className="space-y-2 text-sm">

                  {invoiceData.items.map(
                    (item) => (
                      <div
                        key={item.id}
                        className="so-solid-divider pb-2 so-text-ink"
                      >

                        <div className="flex justify-between">

                          <span>
                            {item.name}
                            {" × "}
                            {item.quantity}
                          </span>

                          <span>
                            {formatCurrency(
                              item.lineTotal
                            )}
                          </span>

                        </div>

                        {invoiceData.gstEnabled &&
                          item.taxRate > 0 && (
                            <div className="flex justify-between text-xs so-text-stone mt-1">

                              <span>
                                GST{" "}
                                {item.taxRate}%
                              </span>

                              <span>
                                {formatCurrency(
                                  item.taxAmount
                                )}
                              </span>

                            </div>
                          )}

                      </div>
                    )
                  )}

                </div>

                <div className="so-dashed-divider my-3" />

                {/* SUBTOTAL */}

                <div className="flex justify-between text-sm so-text-ink">

                  <span>
                    Subtotal
                  </span>

                  <span>
                    {formatCurrency(
                      invoiceData.subtotal
                    )}
                  </span>

                </div>

                {/* GST */}

                {invoiceData.gstEnabled && (
                  <div className="flex justify-between text-sm mt-1 so-text-ink">

                    <span>
                      GST
                    </span>

                    <span>
                      {formatCurrency(
                        invoiceData.taxAmount
                      )}
                    </span>

                  </div>
                )}

                {/* DISCOUNT */}

                {invoiceData.discountAmount >
                  0 && (
                  <div className="flex justify-between text-sm mt-1" style={{ color: "var(--herb-dark)" }}>

                    <span>
                      Discount
                      {invoiceData.discountPercent >
                        0 &&
                        ` (${invoiceData.discountPercent}%)`}
                    </span>

                    <span>
                      -
                      {formatCurrency(
                        invoiceData.discountAmount
                      )}
                    </span>

                  </div>
                )}

                {invoiceData.gstEnabled &&
                  invoiceData.taxInclusive && (
                    <p className="text-xs so-text-stone mt-2">
                      GST included in item prices
                    </p>
                  )}

                {/* TOTAL */}

                <div className="flex justify-between font-bold text-lg mt-3 so-text-ink">

                  <span>
                    Total
                  </span>

                  <span>
                    {formatCurrency(
                      invoiceData.totalAmount
                    )}
                  </span>

                </div>

                <div className="so-dashed-divider mt-3 pt-3 text-sm so-text-ink">
                  <div className="flex justify-between">
                    <span>Cash</span>
                    <span>{formatCurrency(cashAmount)}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span>Online</span>
                    <span>{formatCurrency(onlineAmount)}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span>Total Paid</span>
                    <span>{formatCurrency(roundMoney(toNumber(cashAmount) + toNumber(onlineAmount)))}</span>
                  </div>
                  <div className="flex justify-between font-bold mt-1">
                    <span>Balance Due</span>
                    <span>{formatCurrency(Math.max(0, roundMoney(invoiceData.totalAmount - toNumber(cashAmount) - toNumber(onlineAmount))))}</span>
                  </div>
                </div>

                {/* NOTES */}

                {invoiceData.specialInstructions && (
                  <>
                    <div className="so-dashed-divider my-3" />

                    <p className="text-xs so-text-stone">
                      <strong>
                        Notes:
                      </strong>{" "}
                      {
                        invoiceData.specialInstructions
                      }
                    </p>
                  </>
                )}

                <p className="text-center text-sm so-text-stone mt-5">
                  Thank you for your purchase!
                </p>

              </div>

              {/* PAYMENT SPLIT — saved when Done is pressed */}
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <h4 className="font-semibold text-sm text-slate-800 mb-2">
                  Payment Split
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">
                      Cash Amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={cashAmount}
                      onChange={(event) =>
                        setCashAmount(Math.max(0, toNumber(event.target.value)))
                      }
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-slate-600 mb-1">
                      Online Amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={onlineAmount}
                      onChange={(event) =>
                        setOnlineAmount(Math.max(0, toNumber(event.target.value)))
                      }
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="flex justify-between text-xs text-slate-600 mt-3">
                  <span>Total Paid</span>
                  <span>{formatCurrency(roundMoney(toNumber(cashAmount) + toNumber(onlineAmount)))}</span>
                </div>

                <div className="flex justify-between text-sm font-bold text-slate-800 mt-1">
                  <span>Balance Due</span>
                  <span>{formatCurrency(Math.max(0, roundMoney(invoiceData.totalAmount - toNumber(cashAmount) - toNumber(onlineAmount))))}</span>
                </div>

                {roundMoney(toNumber(cashAmount) + toNumber(onlineAmount)) > roundMoney(invoiceData.totalAmount) && (
                  <p className="text-xs text-red-600 mt-2">
                    Cash + online cannot be greater than the final bill.
                  </p>
                )}
              </div>

              {/* BUTTONS */}

              <div className="mt-5 flex flex-col sm:flex-row gap-3">

                <button
                  type="button"
                  onClick={
                    handleInvoiceDone
                  }
                  disabled={
                    isProcessing ||
                    roundMoney(toNumber(cashAmount) + toNumber(onlineAmount)) >
                      roundMoney(invoiceData.totalAmount)
                  }
                  className="so-invoice-btn-outline flex-1 rounded-lg py-2"
                >
                  {isProcessing
                    ? "Sending..."
                    : "Done"}
                </button>

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className="so-invoice-btn-print flex-1 rounded-lg py-2"
                >
                  Print
                </button>

                <button
                  type="button"
                  onClick={
                    handleDownloadPDF
                  }
                  className="so-invoice-btn-pdf flex-1 rounded-lg py-2"
                >
                  Download PDF
                </button>

              </div>

            </div>

          </div>
        )}
    </>
  );
}







// // src/components/POSView.js

// import React, { useEffect, useRef, useState } from "react";
// import {
//   ShoppingCart,
//   Plus,
//   Minus,
//   Trash2,
// } from "lucide-react";

// import {
//   getAvailableMenuItems,
//   getCategories,
//   getBusinessProfile,
//   getBusinessLogoBlob,
//   getOrderItems,
//   addOrderItem,
//   updateOrderItem,
//   deleteOrderItem,
//   updateOrderStatus,
//   updateOrderType,
//   updateOrderNotes,
// } from "../api";

// import jsPDF from "jspdf";
// import html2canvas from "html2canvas";

// const API_BASE_URL =
//   process.env.REACT_APP_API_URL || "http://localhost:8080";

// // =========================================================
// // DEFAULT "NO IMAGE" ILLUSTRATION — plate & spoon, SwiftOrder style
// // =========================================================

// const PLATE_SPOON_SVG_MARKUP = `
//   <div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f2ede1;">
//     <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
//       <circle cx="32" cy="32" r="27" fill="#fbf9f4" stroke="#ddd4c2" stroke-width="2"/>
//       <circle cx="32" cy="32" r="17" fill="none" stroke="#ddd4c2" stroke-width="2"/>
//       <path d="M17 16 L17 30 C17 32.2 18.8 34 21 34 C23.2 34 25 32.2 25 30 L25 16" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
//       <line x1="21" y1="16" x2="21" y2="48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
//       <path d="M45 16 C41 16 39 20 39 25 C39 29 41 31 45 31 L45 48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round" fill="none"/>
//     </svg>
//   </div>
// `;

// export default function POSView({
//   table,
//   order,
//   onOrderCompleted,
// }) {
//   const [menuItems, setMenuItems] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [selectedCategory, setSelectedCategory] =
//     useState("all");

//   const [cartItems, setCartItems] = useState([]);
//   const [addingItemId, setAddingItemId] = useState(null);
//   const [isProcessing, setIsProcessing] = useState(false);
//   const [cartDataStale, setCartDataStale] = useState(false);

//   const [orderType, setOrderType] = useState(
//     order?.orderType || "DINE_IN"
//   );

//   const [specialInstructions, setSpecialInstructions] =
//     useState(order?.specialInstructions || "");

//   const [discountPercent, setDiscountPercent] =
//     useState(Number(order?.discountPercent || 0));

//   const [discountAmountInput, setDiscountAmountInput] =
//     useState(Number(order?.discountAmount || 0));

//   const [businessProfile, setBusinessProfile] =
//     useState(null);

//   const [logoUrl, setLogoUrl] = useState(null);

//   const [showInvoice, setShowInvoice] = useState(false);
//   const [invoiceData, setInvoiceData] = useState(null);

//   const [showCartDrawer, setShowCartDrawer] =
//     useState(false);

//   const receiptRef = useRef(null);

//   const [isMobile, setIsMobile] = useState(
//     typeof window !== "undefined"
//       ? window.innerWidth < 768
//       : false
//   );

//   // =========================================================
//   // MOBILE
//   // =========================================================

//   useEffect(() => {
//     const resize = () => {
//       setIsMobile(window.innerWidth < 768);
//     };

//     window.addEventListener("resize", resize);

//     return () => {
//       window.removeEventListener("resize", resize);
//     };
//   }, []);

//   // =========================================================
//   // ORDER STATUS
//   // =========================================================

//   const orderStatus = String(
//     order?.status || ""
//   ).toUpperCase();

//   const isEditable =
//     !order ||
//     orderStatus === "DRAFT";

//   const isSentToKitchen =
//     orderStatus === "PENDING" ||
//     orderStatus === "IN_PROGRESS";

//   const isKitchenReady =
//     orderStatus === "READY";

//   // =========================================================
//   // SYNC ORDER
//   // =========================================================

//   useEffect(() => {
//     setOrderType(
//       order?.orderType || "DINE_IN"
//     );

//     setSpecialInstructions(
//       order?.specialInstructions || ""
//     );

//     setDiscountPercent(
//       Number(order?.discountPercent || 0)
//     );

//     setDiscountAmountInput(
//       Number(order?.discountAmount || 0)
//     );
//   }, [order?.id]);

//   // =========================================================
//   // LOAD
//   // =========================================================

//   useEffect(() => {
//     loadMenuItems();
//     loadCategories();
//     loadBusinessProfile();
//   }, []);

//   useEffect(() => {
//     if (order?.id) {
//       loadOrderItems();
//     } else {
//       setCartItems([]);
//     }
//   }, [
//     order?.id,
//     order?.status,
//   ]);

//   // =========================================================
//   // LOAD MENU
//   // =========================================================

//   const loadMenuItems = async () => {
//     try {
//       const data =
//         await getAvailableMenuItems();

//       setMenuItems(data || []);
//     } catch (error) {
//       console.error(
//         "Failed to load menu items:",
//         error
//       );
//     }
//   };

//   // =========================================================
//   // LOAD CATEGORIES
//   // =========================================================

//   const loadCategories = async () => {
//     try {
//       const data =
//         await getCategories();

//       setCategories(data || []);
//     } catch (error) {
//       console.error(
//         "Failed to load categories:",
//         error
//       );
//     }
//   };

//   // =========================================================
//   // LOAD BUSINESS PROFILE
//   // =========================================================

//   const loadBusinessProfile = async () => {
//     try {
//       const data =
//         await getBusinessProfile();

//       if (!data) {
//         return;
//       }

//       setBusinessProfile(data);

//       if (data.hasLogo) {
//         try {
//           const response =
//             await getBusinessLogoBlob();

//           const url =
//             URL.createObjectURL(
//               response.data
//             );

//           setLogoUrl(url);
//         } catch (error) {
//           console.error(
//             "Logo loading failed:",
//             error
//           );
//         }
//       }
//     } catch (error) {
//       console.error(
//         "Business profile error:",
//         error
//       );
//     }
//   };

//   // =========================================================
//   // LOAD ORDER ITEMS
//   // =========================================================

//   const loadOrderItems = async () => {
//     if (!order?.id) {
//       return;
//     }

//     try {
//       const data =
//         await getOrderItems(order.id);

//       setCartItems(data || []);
//       setCartDataStale(false);
//     } catch (error) {
//       console.error(
//         "Failed to load order items:",
//         error
//       );

//       setCartDataStale(true);
//     }
//   };

//   // =========================================================
//   // HELPERS
//   // =========================================================

//   const toNumber = (value) => {
//     const number = Number(value);

//     return Number.isFinite(number)
//       ? number
//       : 0;
//   };

//   const roundMoney = (value) => {
//     return Math.round(
//       (toNumber(value) + Number.EPSILON) * 100
//     ) / 100;
//   };

//   const formatCurrency = (value) => {
//     return `₹${toNumber(value).toFixed(2)}`;
//   };

//   const formatDateTime = (value) => {
//     if (!value) {
//       return "";
//     }

//     const date = new Date(value);

//     return (
//       `${date.toLocaleDateString()} ` +
//       `${date.toLocaleTimeString()}`
//     );
//   };

//   const formatOrderType = (type) => {
//     return type === "TAKEAWAY"
//       ? "🥡 Take Away"
//       : "🍽 Dine In";
//   };

//   // =========================================================
//   // GST
//   // =========================================================

//   const isGstEnabled =
//     businessProfile?.gstEnabled === true;

//   const isTaxInclusive =
//     businessProfile?.taxInclusive === true;

//   const businessDefaultTaxRate =
//     toNumber(
//       businessProfile?.defaultTaxRate
//     );

//   /*
//    * TAX PRIORITY
//    *
//    * 1. OrderItem.taxRate
//    * 2. MenuItem.taxRate
//    * 3. Business defaultTaxRate
//    *
//    * Zero means use business default.
//    */

//   const getTaxRate = (item) => {
//     const orderItemRate =
//       toNumber(item?.taxRate);

//     if (orderItemRate > 0) {
//       return orderItemRate;
//     }

//     const menuItemRate =
//       toNumber(
//         item?.menuItem?.taxRate
//       );

//     if (menuItemRate > 0) {
//       return menuItemRate;
//     }

//     return businessDefaultTaxRate;
//   };

//   // =========================================================
//   // ITEM TAX
//   // =========================================================

//   const calculateItemTax = (item) => {
//     if (!isGstEnabled) {
//       return 0;
//     }

//     const rate =
//       getTaxRate(item);

//     if (rate <= 0) {
//       return 0;
//     }

//     const lineTotal =
//       toNumber(item?.lineTotal);

//     /*
//      * If backend already calculated tax,
//      * use that value.
//      */
//     if (
//       item?.taxAmount !== null &&
//       item?.taxAmount !== undefined &&
//       toNumber(item.taxAmount) > 0
//     ) {
//       return roundMoney(
//         item.taxAmount
//       );
//     }

//     /*
//      * GST INCLUDED
//      *
//      * Example:
//      * Price = ₹525
//      * GST = 5%
//      *
//      * Base = 525 / 1.05 = 500
//      * GST = 25
//      */
//     if (isTaxInclusive) {
//       const base =
//         lineTotal /
//         (1 + rate / 100);

//       return roundMoney(
//         lineTotal - base
//       );
//     }

//     /*
//      * GST EXTRA
//      *
//      * Example:
//      * Price = ₹500
//      * GST = 5%
//      * GST = ₹25
//      * Total = ₹525
//      */
//     return roundMoney(
//       lineTotal * rate / 100
//     );
//   };

//   // =========================================================
//   // ITEM BASE
//   // =========================================================

//   const calculateItemBaseAmount = (item) => {
//     const lineTotal =
//       toNumber(item?.lineTotal);

//     const tax =
//       calculateItemTax(item);

//     if (
//       isGstEnabled &&
//       isTaxInclusive
//     ) {
//       return roundMoney(
//         lineTotal - tax
//       );
//     }

//     return roundMoney(lineTotal);
//   };

//   // =========================================================
//   // CART TOTALS
//   // =========================================================

//   const calculateCartTotals = () => {
//     let subtotal = 0;
//     let taxAmount = 0;
//     let grossTotal = 0;

//     cartItems.forEach((item) => {
//       const lineTotal =
//         toNumber(item?.lineTotal);

//       const tax =
//         calculateItemTax(item);

//       if (
//         isGstEnabled &&
//         isTaxInclusive
//       ) {
//         /*
//          * lineTotal already contains GST.
//          */
//         subtotal +=
//           lineTotal - tax;

//         taxAmount += tax;

//         grossTotal +=
//           lineTotal;
//       } else {
//         /*
//          * lineTotal is item price
//          * before GST.
//          */
//         subtotal +=
//           lineTotal;

//         taxAmount +=
//           tax;

//         grossTotal +=
//           lineTotal + tax;
//       }
//     });

//     subtotal =
//       roundMoney(subtotal);

//     taxAmount =
//       roundMoney(taxAmount);

//     grossTotal =
//       roundMoney(grossTotal);

//     let percent =
//       toNumber(discountPercent);

//     let amount =
//       toNumber(discountAmountInput);

//     if (percent < 0) {
//       percent = 0;
//     }

//     if (percent > 100) {
//       percent = 100;
//     }

//     if (amount < 0) {
//       amount = 0;
//     }

//     if (percent > 0) {
//       amount =
//         roundMoney(
//           grossTotal *
//           percent /
//           100
//         );
//     }

//     amount =
//       Math.min(
//         amount,
//         grossTotal
//       );

//     const totalAmount =
//       roundMoney(
//         Math.max(
//           0,
//           grossTotal - amount
//         )
//       );

//     return {
//       subtotal,
//       taxAmount,
//       grossTotal,
//       discountPercent:
//         roundMoney(percent),
//       discountAmount:
//         amount,
//       totalAmount,
//     };
//   };

//   const cartTotals =
//     calculateCartTotals();

//   // =========================================================
//   // FILTER
//   // =========================================================

//   const filteredItems =
//     selectedCategory === "all"
//       ? menuItems
//       : menuItems.filter(
//           (item) =>
//             item.category &&
//             item.category.id ===
//               selectedCategory
//         );

//   // =========================================================
//   // ADD ITEM
//   // =========================================================

//   const addToCart = async (menuItem) => {
//     if (!order?.id) {
//       alert(
//         "No active order found."
//       );
//       return;
//     }

//     if (!isEditable) {
//       alert(
//         "This order has been sent to the kitchen. Editing is disabled."
//       );
//       return;
//     }

//     if (
//       addingItemId ===
//       menuItem.id
//     ) {
//       return;
//     }

//     setAddingItemId(
//       menuItem.id
//     );

//     try {
//       await addOrderItem(
//         order.id,
//         menuItem.id
//       );

//       await loadOrderItems();

//       if (isMobile) {
//         setShowCartDrawer(true);
//       }
//     } catch (error) {
//       console.error(error);

//       alert(
//         "Unable to add item."
//       );
//     } finally {
//       setAddingItemId(null);
//     }
//   };

//   // =========================================================
//   // QUANTITY
//   // =========================================================

//   const updateQuantity = async (
//     orderItemId,
//     change
//   ) => {
//     if (!isEditable) {
//       return;
//     }

//     try {
//       const item =
//         cartItems.find(
//           (current) =>
//             current.id ===
//             orderItemId
//         );

//       if (!item) {
//         return;
//       }

//       const newQuantity =
//         item.quantity +
//         change;

//       if (newQuantity <= 0) {
//         await deleteOrderItem(
//           orderItemId
//         );
//       } else {
//         await updateOrderItem(
//           orderItemId,
//           newQuantity
//         );
//       }

//       await loadOrderItems();
//     } catch (error) {
//       console.error(error);

//       alert(
//         "Unable to update quantity."
//       );
//     }
//   };

//   // =========================================================
//   // REMOVE
//   // =========================================================

//   const removeFromCart = async (
//     orderItemId
//   ) => {
//     if (!isEditable) {
//       return;
//     }

//     try {
//       await deleteOrderItem(
//         orderItemId
//       );

//       await loadOrderItems();
//     } catch (error) {
//       console.error(error);

//       alert(
//         "Unable to remove item."
//       );
//     }
//   };

//   // =========================================================
//   // ORDER TYPE
//   // =========================================================

//   const handleOrderTypeChange =
//     async (type) => {
//       if (!isEditable) {
//         return;
//       }

//       setOrderType(type);

//       try {
//         await updateOrderType(
//           order.id,
//           type
//         );
//       } catch (error) {
//         console.error(error);

//         alert(
//           "Unable to save order type."
//         );
//       }
//     };

//   // =========================================================
//   // NOTES
//   // =========================================================

//   const handleNotesBlur =
//     async () => {
//       if (
//         !order?.id ||
//         !isEditable
//       ) {
//         return;
//       }

//       try {
//         await updateOrderNotes(
//           order.id,
//           specialInstructions
//         );
//       } catch (error) {
//         console.error(error);

//         alert(
//           "Unable to save notes."
//         );
//       }
//     };

//   // =========================================================
//   // COMPLETE ORDER / INVOICE
//   // =========================================================

//   const handleCompleteOrder =
//     () => {
//       if (!order?.id) {
//         alert(
//           "No active order."
//         );
//         return;
//       }

//       if (
//         cartItems.length === 0
//       ) {
//         alert(
//           "Cart is empty."
//         );
//         return;
//       }

//       const invoiceItems =
//         cartItems.map(
//           (item) => {
//             const taxRate =
//               getTaxRate(item);

//             const taxAmount =
//               calculateItemTax(item);

//             const baseAmount =
//               calculateItemBaseAmount(
//                 item
//               );

//             return {
//               id: item.id,
//               name:
//                 item.menuItem?.name ||
//                 "Item",
//               quantity:
//                 item.quantity,
//               price:
//                 toNumber(item.price),
//               lineTotal:
//                 toNumber(
//                   item.lineTotal
//                 ),
//               baseAmount,
//               taxRate,
//               taxAmount,
//               specialInstructions:
//                 item.specialInstructions ||
//                 "",
//             };
//           }
//         );

//       setInvoiceData({
//         orderNumber:
//           order.orderNumber,

//         orderType,

//         specialInstructions,

//         createdAt:
//           order.createdAt ||
//           new Date().toISOString(),

//         subtotal:
//           cartTotals.subtotal,

//         taxAmount:
//           cartTotals.taxAmount,

//         grossTotal:
//           cartTotals.grossTotal,

//         discountPercent:
//           cartTotals.discountPercent,

//         discountAmount:
//           cartTotals.discountAmount,

//         totalAmount:
//           cartTotals.totalAmount,

//         gstEnabled:
//           isGstEnabled,

//         taxInclusive:
//           isTaxInclusive,

//         items:
//           invoiceItems,
//       });

//       setShowInvoice(true);
//     };

//   // =========================================================
//   // DONE / SEND KITCHEN
//   // =========================================================

//   const handleInvoiceDone =
//     async () => {
//       if (!order?.id) {
//         setShowInvoice(false);
//         return;
//       }

//       setIsProcessing(true);

//       try {
//         await updateOrderType(
//           order.id,
//           orderType
//         );

//         await updateOrderNotes(
//           order.id,
//           specialInstructions
//         );

//         /*
//          * Keep calculated values available
//          * to the parent UI.
//          */
//         order.discountPercent =
//           cartTotals.discountPercent;

//         order.discountAmount =
//           cartTotals.discountAmount;

//         order.subtotal =
//           cartTotals.subtotal;

//         order.taxAmount =
//           cartTotals.taxAmount;

//         order.totalAmount =
//           cartTotals.totalAmount;

//         /*
//          * IMPORTANT:
//          *
//          * The backend OrderService must calculate and
//          * persist OrderItem.taxRate, OrderItem.taxAmount,
//          * Order.taxAmount and Order.totalAmount when
//          * the order is completed.
//          *
//          * Frontend only displays the calculated values.
//          */
//         await updateOrderStatus(
//           order.id,
//           "PENDING"
//         );

//         if (
//           typeof onOrderCompleted ===
//           "function"
//         ) {
//           onOrderCompleted(order);
//         }

//         setCartItems([]);

//         setSpecialInstructions("");

//         setOrderType(
//           "DINE_IN"
//         );

//         setDiscountPercent(0);

//         setDiscountAmountInput(0);

//         setInvoiceData(null);

//         setShowInvoice(false);

//         if (isMobile) {
//           setShowCartDrawer(false);
//         }
//       } catch (error) {
//         console.error(error);

//         alert(
//           "Unable to send order to kitchen."
//         );
//       } finally {
//         setIsProcessing(false);
//       }
//     };

//   // =========================================================
//   // PRINT
//   // =========================================================

//   const handlePrint = () => {
//     if (!receiptRef.current) {
//       return;
//     }

//     const content =
//       receiptRef.current.innerHTML;

//     const printWindow =
//       window.open(
//         "",
//         "_blank",
//         "width=400,height=700"
//       );

//     if (!printWindow) {
//       alert(
//         "Popup blocked. Allow popups to print invoice."
//       );
//       return;
//     }

//     printWindow.document.open();

//     printWindow.document.write(`
//       <html>
//         <head>
//           <title>GST Invoice</title>

//           <meta
//             name="viewport"
//             content="width=device-width, initial-scale=1"
//           />

//           <style>
//             body {
//               font-family:
//                 Arial,
//                 sans-serif;
//               padding: 16px;
//               background: white;
//               color: #111827;
//             }

//             .line {
//               display: flex;
//               justify-content: space-between;
//               margin: 4px 0;
//             }

//             .center {
//               text-align: center;
//             }

//             .border-top {
//               border-top:
//                 1px dashed #000;
//               margin-top: 8px;
//               padding-top: 8px;
//             }

//             img {
//               max-width: 100%;
//               height: auto;
//             }
//           </style>
//         </head>

//         <body>
//           ${content}
//         </body>
//       </html>
//     `);

//     printWindow.document.close();

//     printWindow.focus();

//     setTimeout(() => {
//       printWindow.print();
//       printWindow.close();
//     }, 300);
//   };

//   // =========================================================
//   // PDF
//   // =========================================================

//   const handleDownloadPDF =
//     async () => {
//       if (
//         !receiptRef.current ||
//         !invoiceData
//       ) {
//         return;
//       }

//       const element =
//         receiptRef.current;

//       const originalWidth =
//         element.style.width;

//       const originalMaxHeight =
//         element.style.maxHeight;

//       try {
//         element.style.width =
//           window.innerWidth > 900
//             ? "700px"
//             : `${Math.floor(
//                 window.innerWidth * 0.92
//               )}px`;

//         element.style.maxHeight =
//           "none";

//         const canvas =
//           await html2canvas(
//             element,
//             {
//               scale:
//                 Math.min(
//                   2,
//                   window.devicePixelRatio ||
//                     1.5
//                 ) * 1.2,

//               useCORS: true,

//               scrollX: 0,

//               scrollY:
//                 -window.scrollY,

//               windowWidth:
//                 element.scrollWidth,

//               windowHeight:
//                 element.scrollHeight,
//             }
//           );

//         const imgData =
//           canvas.toDataURL(
//             "image/png"
//           );

//         const pdf =
//           new jsPDF(
//             "p",
//             "pt",
//             "a4"
//           );

//         const pageWidth =
//           pdf.internal.pageSize.getWidth();

//         const pageHeight =
//           pdf.internal.pageSize.getHeight();

//         const margin = 20;

//         const imgWidth =
//           pageWidth -
//           margin * 2;

//         const imgHeight =
//           (canvas.height *
//             imgWidth) /
//           canvas.width;

//         let heightLeft =
//           imgHeight;

//         let position =
//           margin;

//         pdf.addImage(
//           imgData,
//           "PNG",
//           margin,
//           position,
//           imgWidth,
//           imgHeight
//         );

//         heightLeft -=
//           pageHeight -
//           margin * 2;

//         while (
//           heightLeft > 0
//         ) {
//           pdf.addPage();

//           position =
//             margin -
//             (
//               imgHeight -
//               heightLeft
//             );

//           pdf.addImage(
//             imgData,
//             "PNG",
//             margin,
//             position,
//             imgWidth,
//             imgHeight
//           );

//           heightLeft -=
//             pageHeight -
//             margin * 2;
//         }

//         pdf.save(
//           `invoice_${
//             invoiceData.orderNumber ||
//             Date.now()
//           }.pdf`
//         );
//       } catch (error) {
//         console.error(
//           "PDF generation failed:",
//           error
//         );

//         alert(
//           "Failed to generate PDF."
//         );
//       } finally {
//         element.style.width =
//           originalWidth || "";

//         element.style.maxHeight =
//           originalMaxHeight || "";
//       }
//     };

//   // =========================================================
//   // ORDER BUTTON
//   // =========================================================

//   const renderOrderActionButton =
//     ({ mobile = false } = {}) => {
//       if (!order) {
//         return null;
//       }

//       const baseClass =
//         mobile
//           ? "flex-1 rounded-lg py-2 text-sm font-semibold"
//           : "w-full py-3 rounded-lg font-semibold";

//       if (
//         orderStatus === "DRAFT"
//       ) {
//         return (
//           <button
//             type="button"
//             onClick={
//               handleCompleteOrder
//             }
//             disabled={
//               cartItems.length === 0 ||
//               isProcessing
//             }
//             className={`${baseClass} so-btn-primary`}
//           >
//             🧾 Complete Order
//           </button>
//         );
//       }

//       if (
//         orderStatus === "PENDING" ||
//         orderStatus === "IN_PROGRESS"
//       ) {
//         return (
//           <button
//             type="button"
//             disabled
//             className={`${baseClass} so-btn-waiting`}
//           >
//             ⏳ Waiting For Kitchen
//           </button>
//         );
//       }

//       if (
//         orderStatus === "READY"
//       ) {
//         return (
//           <button
//             type="button"
//             disabled
//             className={`${baseClass} so-btn-ready`}
//           >
//             🍽 Kitchen Finished
//           </button>
//         );
//       }

//       return null;
//     };

//   // =========================================================
//   // RENDER
//   // =========================================================

//   return (
//     <>
//       {/* Scoped SwiftOrder theme — same tokens as Login.js,
//           Dashboard.js, Sidebar.js and DirectPOSView.js. */}
//       <style>{`
//         @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

//         .so-pos {
//           --ink: #1c1a17;
//           --paper: #fbf9f4;
//           --paper-dim: #f2ede1;
//           --chili: #e4572e;
//           --chili-dark: #c43f1a;
//           --herb: #3f6b4f;
//           --herb-dark: #2d4f39;
//           --mustard: #f2a93b;
//           --stone: #6b6459;
//           --stone-light: #a49c8d;
//           --line: #ddd4c2;
//           --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
//           --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
//           --font-mono: 'JetBrains Mono', 'Courier New', monospace;

//           background: var(--paper-dim);
//           font-family: var(--font-body);
//           color: var(--ink);
//         }

//         .so-pos * {
//           box-sizing: border-box;
//         }

//         .so-text-ink { color: var(--ink); }
//         .so-text-stone { color: var(--stone); }
//         .so-text-stone-light { color: var(--stone-light); }
//         .so-text-herb { color: var(--herb-dark); }

//         .so-page-title {
//           font-family: var(--font-display);
//           font-size: 1.9rem;
//           letter-spacing: 0.01em;
//           color: var(--ink);
//         }

//         .so-subtitle {
//           font-size: 0.85rem;
//           color: var(--stone);
//         }

//         .so-cart-toggle {
//           display: flex;
//           align-items: center;
//           gap: 8px;
//           background: var(--chili);
//           color: var(--paper);
//           padding: 10px 14px;
//           border-radius: 8px;
//           font-weight: 700;
//           font-size: 0.88rem;
//           box-shadow: 0 8px 18px -8px rgba(228, 87, 46, 0.55);
//         }

//         .so-status-strip {
//           border-radius: 8px;
//           padding: 12px 16px;
//           margin-bottom: 16px;
//           font-size: 0.88rem;
//           border: 1px solid var(--line);
//         }

//         .so-status-gst-on {
//           background: #eaf3ee;
//           border-color: var(--herb);
//           color: var(--herb-dark);
//         }

//         .so-status-gst-off {
//           background: var(--paper);
//           color: var(--stone);
//         }

//         .so-status-stale {
//           background: #fdf3e2;
//           border: 1px solid var(--mustard);
//           color: #854f0b;
//           border-radius: 8px;
//           padding: 12px;
//           margin-bottom: 16px;
//           display: flex;
//           justify-content: space-between;
//         }

//         .so-status-sent {
//           background: var(--paper-dim);
//           border: 1px solid var(--line);
//           color: var(--stone);
//           border-radius: 8px;
//           padding: 12px;
//           margin-bottom: 16px;
//         }

//         .so-status-ready {
//           background: #eaf3ee;
//           border: 1px solid var(--herb);
//           color: var(--herb-dark);
//           border-radius: 8px;
//           padding: 12px;
//           margin-bottom: 16px;
//         }

//         .so-table-banner {
//           background: #fdece6;
//           border: 1px solid var(--chili);
//           border-radius: 12px;
//           padding: 16px;
//           margin-bottom: 20px;
//         }

//         .so-table-badge {
//           background: var(--chili);
//           color: var(--paper);
//           padding: 8px 16px;
//           border-radius: 999px;
//           font-weight: 700;
//           font-family: var(--font-mono);
//           font-size: 0.78rem;
//         }

//         .so-type-toggle {
//           padding: 9px 18px;
//           border-radius: 8px;
//           border: 1.5px solid var(--line);
//           background: #fff;
//           color: var(--stone);
//           font-weight: 600;
//           transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
//         }

//         .so-type-toggle:hover:not(:disabled) {
//           border-color: var(--chili);
//         }

//         .so-type-toggle-active {
//           background: var(--chili);
//           color: var(--paper);
//           border-color: var(--chili);
//         }

//         .so-category-pill {
//           padding: 8px 14px;
//           border-radius: 999px;
//           white-space: nowrap;
//           font-size: 0.85rem;
//           font-weight: 600;
//           background: #fff;
//           color: var(--stone);
//           border: 1px solid var(--line);
//           transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
//         }

//         .so-category-pill:hover {
//           border-color: var(--chili);
//         }

//         .so-category-pill-active {
//           background: var(--ink);
//           color: var(--paper);
//           border-color: var(--ink);
//         }

//         .so-item-card {
//           background: #fffdf8;
//           border-radius: 12px;
//           border: 1px solid var(--line);
//           padding: 12px;
//           text-align: left;
//           transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
//         }

//         .so-item-card:hover:not(:disabled) {
//           border-color: var(--chili);
//           transform: translateY(-2px);
//           box-shadow: 0 14px 24px -18px rgba(28, 26, 23, 0.4);
//         }

//         .so-item-card:disabled {
//           opacity: 0.5;
//         }

//         .so-item-image-wrap {
//           background: var(--paper-dim);
//           border-radius: 8px;
//         }

//         .so-no-image {
//           width: 100%;
//           height: 100%;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           background: var(--paper-dim);
//         }

//         .so-item-price {
//           font-weight: 700;
//           color: var(--herb-dark);
//         }

//         .so-item-gst {
//           font-size: 0.7rem;
//           color: var(--stone-light);
//         }

//         .so-cart-aside {
//           background: #fffdf8;
//           box-shadow: -8px 0 24px -20px rgba(28, 26, 23, 0.4);
//           border-left: 1px solid var(--line);
//         }

//         .so-cart-empty {
//           color: var(--stone-light);
//         }

//         .so-cart-line {
//           background: var(--paper-dim);
//         }

//         .so-remove-btn {
//           color: var(--chili);
//         }

//         .so-remove-btn:disabled {
//           color: var(--stone-light);
//         }

//         .so-qty-btn {
//           width: 32px;
//           height: 32px;
//           background: #fff;
//           border-radius: 6px;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           border: 1px solid var(--line);
//           color: var(--ink);
//         }

//         .so-qty-btn:hover:not(:disabled) {
//           background: var(--paper-dim);
//         }

//         .so-qty-btn:disabled {
//           opacity: 0.4;
//         }

//         .so-textarea, .so-text-input {
//           border: 1px solid var(--line);
//           background: var(--paper);
//           color: var(--ink);
//           outline: none;
//           transition: border-color 0.15s ease, box-shadow 0.15s ease;
//         }

//         .so-textarea:focus, .so-text-input:focus {
//           border-color: var(--chili);
//           box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
//         }

//         .so-textarea:disabled {
//           background: var(--paper-dim);
//         }

//         .so-dashed-divider {
//           border-top: 1px dashed var(--line);
//         }

//         .so-solid-divider {
//           border-top: 1px solid var(--line);
//         }

//         .so-discount-panel {
//           background: var(--paper-dim);
//           border: 1px solid var(--line);
//           border-radius: 8px;
//           padding: 12px;
//         }

//         .so-btn-primary {
//           background: var(--chili);
//           color: var(--paper);
//           box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
//           transition: background 0.15s ease, transform 0.1s ease, opacity 0.15s ease;
//         }

//         .so-btn-primary:hover:not(:disabled) {
//           background: var(--chili-dark);
//         }

//         .so-btn-primary:disabled {
//           background: var(--stone-light);
//           cursor: not-allowed;
//           box-shadow: none;
//         }

//         .so-btn-waiting {
//           background: var(--mustard);
//           color: #412402;
//           cursor: not-allowed;
//         }

//         .so-btn-ready {
//           background: var(--herb);
//           color: var(--paper);
//           cursor: not-allowed;
//         }

//         .so-mobile-bar {
//           background: #fffdf8;
//           border-top: 1px solid var(--line);
//         }

//         .so-mobile-cart-chip {
//           background: var(--paper-dim);
//           color: var(--ink);
//           padding: 8px 12px;
//           border-radius: 8px;
//           font-size: 0.85rem;
//           font-weight: 600;
//         }

//         .so-drawer-overlay {
//           background: rgba(28, 26, 23, 0.5);
//         }

//         .so-drawer {
//           background: #fffdf8;
//         }

//         .so-invoice-overlay {
//           background: rgba(28, 26, 23, 0.6);
//         }

//         .so-invoice-card {
//           background: #fffdf8;
//           border-radius: 12px;
//         }

//         .so-invoice-title {
//           font-family: var(--font-display);
//           font-size: 1.3rem;
//           color: var(--ink);
//         }

//         .so-invoice-btn-outline {
//           border: 1px solid var(--line);
//           color: var(--ink);
//           background: #fff;
//           transition: background 0.15s ease;
//         }

//         .so-invoice-btn-outline:hover:not(:disabled) {
//           background: var(--paper-dim);
//         }

//         .so-invoice-btn-print {
//           background: var(--herb);
//           color: var(--paper);
//         }

//         .so-invoice-btn-print:hover:not(:disabled) {
//           background: var(--herb-dark);
//         }

//         .so-invoice-btn-pdf {
//           background: var(--ink);
//           color: var(--paper);
//         }

//         .so-invoice-btn-pdf:hover:not(:disabled) {
//           background: #302c27;
//         }
//       `}</style>

//       <div className="so-pos flex flex-col md:flex-row h-full">

//         {/* ===================================================
//             LEFT
//         =================================================== */}

//         <div className="flex-1 p-4 md:p-6 overflow-auto">

//           {/* HEADER */}

//           <div className="flex items-center justify-between mb-4">

//             <div>
//               <h2 className="so-page-title">
//                 Point of Sale
//               </h2>

//               <p className="so-subtitle mt-1">
//                 Restaurant POS
//               </p>
//             </div>

//             {isMobile && (
//               <button
//                 type="button"
//                 onClick={() =>
//                   setShowCartDrawer(true)
//                 }
//                 className="so-cart-toggle"
//               >
//                 <ShoppingCart
//                   size={18}
//                 />

//                 <span>
//                   Cart ({cartItems.length})
//                 </span>
//               </button>
//             )}
//           </div>

//           {/* GST STATUS */}

//           {businessProfile && (
//             <div
//               className={`so-status-strip ${
//                 isGstEnabled &&
//                 businessDefaultTaxRate > 0
//                   ? "so-status-gst-on"
//                   : "so-status-gst-off"
//               }`}
//             >
//               {isGstEnabled &&
//               businessDefaultTaxRate > 0 ? (
//                 <>
//                   <strong>
//                     GST Enabled
//                   </strong>

//                   {" • "}

//                   Default GST:{" "}
//                   <strong>
//                     {businessDefaultTaxRate}%
//                   </strong>

//                   {isTaxInclusive && (
//                     <>
//                       {" • "}
//                       GST included in prices
//                     </>
//                   )}
//                 </>
//               ) : (
//                 "GST Disabled"
//               )}
//             </div>
//           )}

//           {/* STALE */}

//           {cartDataStale && (
//             <div className="so-status-stale">
//               <span>
//                 Cart data may be out of date.
//               </span>

//               <button
//                 type="button"
//                 onClick={
//                   loadOrderItems
//                 }
//                 className="underline font-semibold"
//               >
//                 Retry
//               </button>
//             </div>
//           )}

//           {/* KITCHEN STATUS */}

//           {isSentToKitchen && (
//             <div className="so-status-sent">
//               This order has been sent to the kitchen.
//               Editing is disabled until the kitchen finishes.
//             </div>
//           )}

//           {isKitchenReady && (
//             <div className="so-status-ready">
//               Kitchen has finished this order.
//               It is ready for billing.
//             </div>
//           )}

//           {/* TABLE */}

//           {table && (
//             <div className="so-table-banner">

//               <div className="flex justify-between items-center">

//                 <div>

//                   <h3 className="text-xl font-bold" style={{ color: "var(--chili-dark)" }}>
//                     🍽 {table.tableName}
//                   </h3>

//                   <p className="mt-1" style={{ color: "var(--chili-dark)" }}>
//                     Capacity: {table.capacity}
//                   </p>

//                   <p style={{ color: "var(--chili-dark)" }}>
//                     Order:{" "}
//                     {order?.orderNumber}
//                   </p>

//                 </div>

//                 <div className="so-table-badge">
//                   {table.status}
//                 </div>

//               </div>
//             </div>
//           )}

//           {/* ORDER TYPE */}

//           {order && (
//             <div className="mb-5">

//               <p className="text-sm font-semibold so-text-ink mb-2">
//                 Order Type
//               </p>

//               <div className="flex gap-3">

//                 <button
//                   type="button"
//                   onClick={() =>
//                     handleOrderTypeChange(
//                       "DINE_IN"
//                     )
//                   }
//                   disabled={!isEditable}
//                   className={`so-type-toggle ${
//                     orderType === "DINE_IN"
//                       ? "so-type-toggle-active"
//                       : ""
//                   }`}
//                 >
//                   🍽 Dine In
//                 </button>

//                 <button
//                   type="button"
//                   onClick={() =>
//                     handleOrderTypeChange(
//                       "TAKEAWAY"
//                     )
//                   }
//                   disabled={!isEditable}
//                   className={`so-type-toggle ${
//                     orderType === "TAKEAWAY"
//                       ? "so-type-toggle-active"
//                       : ""
//                   }`}
//                 >
//                   🥡 Take Away
//                 </button>

//               </div>
//             </div>
//           )}

//           {/* CATEGORIES */}

//           <div className="flex gap-2 mb-4 overflow-x-auto pb-2">

//             <button
//               type="button"
//               onClick={() =>
//                 setSelectedCategory(
//                   "all"
//                 )
//               }
//               className={`so-category-pill ${
//                 selectedCategory === "all"
//                   ? "so-category-pill-active"
//                   : ""
//               }`}
//             >
//               All Items
//             </button>

//             {categories.map(
//               (category) => (
//                 <button
//                   type="button"
//                   key={category.id}
//                   onClick={() =>
//                     setSelectedCategory(
//                       category.id
//                     )
//                   }
//                   className={`so-category-pill ${
//                     selectedCategory === category.id
//                       ? "so-category-pill-active"
//                       : ""
//                   }`}
//                 >
//                   {category.name}
//                 </button>
//               )
//             )}

//           </div>

//           {/* MENU */}

//           <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">

//             {filteredItems.map(
//               (item) => {

//                 const adding =
//                   addingItemId ===
//                   item.id;

//                 const itemTaxRate =
//                   toNumber(
//                     item.taxRate
//                   ) > 0
//                     ? toNumber(
//                         item.taxRate
//                       )
//                     : businessDefaultTaxRate;

//                 const displayPrice =
//                   toNumber(
//                     item.price
//                   );

//                 const gstAmount =
//                   isGstEnabled &&
//                   !isTaxInclusive
//                     ? roundMoney(
//                         displayPrice *
//                         itemTaxRate /
//                         100
//                       )
//                     : 0;

//                 const finalPrice =
//                   isGstEnabled &&
//                   !isTaxInclusive
//                     ? roundMoney(
//                         displayPrice +
//                         gstAmount
//                       )
//                     : displayPrice;

//                 return (
//                   <button
//                     type="button"
//                     key={item.id}
//                     onClick={() =>
//                       addToCart(item)
//                     }
//                     disabled={
//                       adding ||
//                       !isEditable
//                     }
//                     className="so-item-card"
//                   >

//                     <div className="so-item-image-wrap h-28 md:h-32 mb-3 overflow-hidden flex items-center justify-center">

//                       <img
//                         src={`${API_BASE_URL}/api/menu-items/${item.id}/image`}
//                         alt={item.name}
//                         className="w-full h-full object-cover"
//                         onError={(event) => {

//                           if (
//                             item.imageUrl
//                           ) {
//                             event.currentTarget.src =
//                               item.imageUrl;
//                           } else {
//                             event.currentTarget.style.display =
//                               "none";

//                             const parent =
//                               event.currentTarget
//                                 .parentElement;

//                             if (parent) {
//                               parent.innerHTML =
//                                 PLATE_SPOON_SVG_MARKUP;
//                             }
//                           }

//                         }}
//                       />

//                     </div>

//                     <h3 className="font-semibold so-text-ink">
//                       {item.name}
//                     </h3>

//                     <div className="mt-2">

//                       <p className="text-lg so-item-price">
//                         {formatCurrency(
//                           finalPrice
//                         )}
//                       </p>

//                       {isGstEnabled &&
//                         itemTaxRate > 0 && (
//                           <p className="so-item-gst mt-1">
//                             GST {itemTaxRate}%
//                             {!isTaxInclusive &&
//                               ` • ${formatCurrency(
//                                 gstAmount
//                               )}`}
//                             {isTaxInclusive &&
//                               " included"}
//                           </p>
//                         )}

//                     </div>

//                   </button>
//                 );
//               }
//             )}

//           </div>
//         </div>

//         {/* ===================================================
//             DESKTOP CART
//         =================================================== */}

//         <aside className="so-cart-aside hidden md:flex md:w-96 p-4 md:p-6 flex-col">

//           <div className="flex items-center gap-2 mb-4">

//             <ShoppingCart
//               size={24}
//               color="#1c1a17"
//             />

//             <h3 className="so-page-title" style={{ fontSize: "1.5rem" }}>
//               Cart
//             </h3>

//           </div>

//           <div className="flex-1 overflow-auto mb-4">

//             {cartItems.length === 0 ? (
//               <div className="so-cart-empty text-center mt-12">

//                 <ShoppingCart
//                   size={48}
//                   className="mx-auto mb-2 opacity-50"
//                 />

//                 <p>
//                   Cart is empty
//                 </p>

//               </div>
//             ) : (
//               <div className="space-y-3">

//                 {cartItems.map(
//                   (item) => {

//                     const taxRate =
//                       getTaxRate(item);

//                     const tax =
//                       calculateItemTax(
//                         item
//                       );

//                     const finalLineTotal =
//                       isGstEnabled &&
//                       !isTaxInclusive
//                         ? roundMoney(
//                             toNumber(
//                               item.lineTotal
//                             ) + tax
//                           )
//                         : toNumber(
//                             item.lineTotal
//                           );

//                     return (
//                       <div
//                         key={item.id}
//                         className="so-cart-line rounded-lg p-3"
//                       >

//                         <div className="flex justify-between items-start mb-2">

//                           <div>

//                             <h4 className="font-semibold so-text-ink">
//                               {
//                                 item.menuItem
//                                   ?.name
//                               }
//                             </h4>

//                             {isGstEnabled &&
//                               taxRate > 0 && (
//                                 <p className="text-xs so-text-stone">
//                                   GST{" "}
//                                   {taxRate}%
//                                   {" • "}
//                                   {formatCurrency(
//                                     tax
//                                   )}
//                                 </p>
//                               )}

//                           </div>

//                           <button
//                             type="button"
//                             onClick={() =>
//                               removeFromCart(
//                                 item.id
//                               )
//                             }
//                             disabled={
//                               !isEditable
//                             }
//                             className="so-remove-btn"
//                           >
//                             <Trash2
//                               size={16}
//                             />
//                           </button>

//                         </div>

//                         <div className="flex justify-between items-center">

//                           <div className="flex items-center gap-2">

//                             <button
//                               type="button"
//                               onClick={() =>
//                                 updateQuantity(
//                                   item.id,
//                                   -1
//                                 )
//                               }
//                               disabled={
//                                 !isEditable
//                               }
//                               className="so-qty-btn"
//                             >
//                               <Minus
//                                 size={16}
//                               />
//                             </button>

//                             <span className="w-8 text-center font-medium so-text-ink">
//                               {item.quantity}
//                             </span>

//                             <button
//                               type="button"
//                               onClick={() =>
//                                 updateQuantity(
//                                   item.id,
//                                   1
//                                 )
//                               }
//                               disabled={
//                                 !isEditable
//                               }
//                               className="so-qty-btn"
//                             >
//                               <Plus
//                                 size={16}
//                               />
//                             </button>

//                           </div>

//                           <span className="font-bold so-text-ink">
//                             {formatCurrency(
//                               finalLineTotal
//                             )}
//                           </span>

//                         </div>

//                       </div>
//                     );
//                   }
//                 )}

//               </div>
//             )}

//           </div>

//           {/* NOTES */}

//           {order && (
//             <div className="mb-4">

//               <label className="text-sm font-semibold so-text-ink mb-1 block">
//                 Special Instructions
//               </label>

//               <textarea
//                 value={
//                   specialInstructions
//                 }
//                 onChange={(event) =>
//                   setSpecialInstructions(
//                     event.target.value
//                   )
//                 }
//                 onBlur={
//                   handleNotesBlur
//                 }
//                 disabled={
//                   !isEditable
//                 }
//                 rows={2}
//                 placeholder="Extra sauce, no onion, less spicy..."
//                 className="so-textarea w-full text-sm rounded-lg p-2"
//               />

//             </div>
//           )}

//           {/* TOTALS */}

//           <div className="so-solid-divider pt-4">

//             <div className="flex justify-between text-sm mb-2 so-text-ink">

//               <span>
//                 Subtotal
//               </span>

//               <span className="font-medium">
//                 {formatCurrency(
//                   cartTotals.subtotal
//                 )}
//               </span>

//             </div>

//             {isGstEnabled && (
//               <div className="flex justify-between text-sm mb-3 so-text-ink">

//                 <span>
//                   GST
//                 </span>

//                 <span className="font-medium">
//                   {formatCurrency(
//                     cartTotals.taxAmount
//                   )}
//                 </span>

//               </div>
//             )}

//             {isEditable && (
//               <div className="so-discount-panel mb-3">

//                 <div className="text-sm font-semibold so-text-ink mb-2">
//                   Discount
//                 </div>

//                 <div className="grid grid-cols-2 gap-2">

//                   <div>
//                     <label className="text-xs font-medium so-text-stone mb-1 block">
//                       Discount Percentage (%)
//                     </label>

//                     <input
//                       type="number"
//                       min="0"
//                       max="100"
//                       step="0.01"
//                       value={
//                         discountPercent
//                       }
//                       onChange={(event) => {
//                         setDiscountPercent(
//                           Math.min(
//                             100,
//                             Math.max(
//                               0,
//                               Number(
//                                 event.target.value
//                               ) || 0
//                             )
//                           )
//                         );
//                       }}
//                       placeholder="Discount %"
//                       className="so-text-input w-full rounded-lg px-2 py-2 text-sm"
//                     />
//                   </div>

//                   <div>
//                     <label className="text-xs font-medium so-text-stone mb-1 block">
//                       Fixed Amount (₹)
//                     </label>

//                     <input
//                       type="number"
//                       min="0"
//                       step="0.01"
//                       value={
//                         discountPercent > 0
//                           ? cartTotals.discountAmount
//                           : discountAmountInput
//                       }
//                       onChange={(event) => {
//                         setDiscountPercent(
//                           0
//                         );

//                         setDiscountAmountInput(
//                           Math.max(
//                             0,
//                             Number(
//                               event.target.value
//                             ) || 0
//                           )
//                         );
//                       }}
//                       placeholder="Amount ₹"
//                       className="so-text-input w-full rounded-lg px-2 py-2 text-sm"
//                     />
//                   </div>

//                 </div>

//               </div>
//             )}

//             {cartTotals.discountAmount >
//               0 && (
//               <div className="flex justify-between text-sm mb-3" style={{ color: "var(--herb-dark)" }}>

//                 <span>
//                   Discount
//                   {cartTotals.discountPercent >
//                     0 &&
//                     ` (${cartTotals.discountPercent}%)`}
//                 </span>

//                 <span>
//                   -
//                   {formatCurrency(
//                     cartTotals.discountAmount
//                   )}
//                 </span>

//               </div>
//             )}

//             <div className="flex justify-between items-center mb-3">

//               <span className="text-xl font-bold so-text-ink">
//                 Total
//               </span>

//               <span className="text-2xl font-bold so-text-herb">
//                 {formatCurrency(
//                   cartTotals.totalAmount
//                 )}
//               </span>

//             </div>

//             {renderOrderActionButton()}

//           </div>

//         </aside>

//         {/* ===================================================
//             MOBILE BAR
//         =================================================== */}

//         {isMobile && (
//           <div className="so-mobile-bar fixed bottom-0 left-0 right-0 p-3 flex items-center gap-3 z-40">

//             <div className="flex-1">

//               <div className="text-sm so-text-stone">
//                 Total
//               </div>

//               <div className="text-lg font-bold so-text-herb">
//                 {formatCurrency(
//                   cartTotals.totalAmount
//                 )}
//               </div>

//             </div>

//             <button
//               type="button"
//               onClick={() =>
//                 setShowCartDrawer(true)
//               }
//               className="so-mobile-cart-chip"
//             >
//               Cart ({
//                 cartItems.length
//               })
//             </button>

//             {renderOrderActionButton({
//               mobile: true,
//             })}

//           </div>
//         )}

//       </div>

//       {/* =====================================================
//           MOBILE CART
//       ===================================================== */}

//       {isMobile &&
//         showCartDrawer && (
//           <div className="fixed inset-0 z-50 flex">

//             <div
//               className="absolute inset-0 so-drawer-overlay"
//               onClick={() =>
//                 setShowCartDrawer(false)
//               }
//             />

//             <div className="so-drawer relative w-full max-h-[90vh] rounded-t-xl shadow-xl mt-auto overflow-auto p-4">

//               <div className="flex justify-between mb-4">

//                 <h3 className="text-lg font-semibold so-text-ink">
//                   Cart
//                 </h3>

//                 <button
//                   type="button"
//                   onClick={() =>
//                     setShowCartDrawer(false)
//                   }
//                   className="so-text-stone"
//                 >
//                   Close
//                 </button>

//               </div>

//               {!isEditable && (
//                 <div className="so-status-sent">
//                   This order has been sent to the kitchen.
//                   Editing is disabled.
//                 </div>
//               )}

//               {cartItems.map(
//                 (item) => {

//                   const taxRate =
//                     getTaxRate(item);

//                   const tax =
//                     calculateItemTax(
//                       item
//                     );

//                   const finalLineTotal =
//                     isGstEnabled &&
//                     !isTaxInclusive
//                       ? roundMoney(
//                           toNumber(
//                             item.lineTotal
//                           ) + tax
//                         )
//                       : toNumber(
//                           item.lineTotal
//                         );

//                   return (
//                     <div
//                       key={item.id}
//                       className="so-cart-line rounded-lg p-3 mb-3"
//                     >

//                       <div className="flex justify-between">

//                         <div>

//                           <h4 className="font-semibold so-text-ink">
//                             {
//                               item.menuItem
//                                 ?.name
//                             }
//                           </h4>

//                           {isGstEnabled &&
//                             taxRate > 0 && (
//                               <p className="text-xs so-text-stone">
//                                 GST{" "}
//                                 {taxRate}%
//                                 {" • "}
//                                 {formatCurrency(
//                                   tax
//                                 )}
//                               </p>
//                             )}

//                         </div>

//                         <button
//                           type="button"
//                           onClick={() =>
//                             removeFromCart(
//                               item.id
//                             )
//                           }
//                           disabled={
//                             !isEditable
//                           }
//                           className="so-remove-btn"
//                         >
//                           <Trash2
//                             size={16}
//                           />
//                         </button>

//                       </div>

//                       <div className="flex justify-between items-center mt-3">

//                         <div className="flex items-center gap-2">

//                           <button
//                             type="button"
//                             onClick={() =>
//                               updateQuantity(
//                                 item.id,
//                                 -1
//                               )
//                             }
//                             disabled={
//                               !isEditable
//                             }
//                             className="so-qty-btn"
//                           >
//                             <Minus
//                               size={16}
//                               className="mx-auto"
//                             />
//                           </button>

//                           <span className="so-text-ink">
//                             {item.quantity}
//                           </span>

//                           <button
//                             type="button"
//                             onClick={() =>
//                               updateQuantity(
//                                 item.id,
//                                 1
//                               )
//                             }
//                             disabled={
//                               !isEditable
//                             }
//                             className="so-qty-btn"
//                           >
//                             <Plus
//                               size={16}
//                               className="mx-auto"
//                             />
//                           </button>

//                         </div>

//                         <strong className="so-text-ink">
//                           {formatCurrency(
//                             finalLineTotal
//                           )}
//                         </strong>

//                       </div>

//                     </div>
//                   );
//                 }
//               )}

//               {/* MOBILE NOTES */}

//               {order && (
//                 <div className="mb-4">

//                   <label className="text-sm font-semibold so-text-ink block mb-1">
//                     Special Instructions
//                   </label>

//                   <textarea
//                     value={
//                       specialInstructions
//                     }
//                     onChange={(event) =>
//                       setSpecialInstructions(
//                         event.target.value
//                       )
//                     }
//                     onBlur={
//                       handleNotesBlur
//                     }
//                     disabled={
//                       !isEditable
//                     }
//                     rows={2}
//                     placeholder="Extra sauce, no onion..."
//                     className="so-textarea w-full rounded-lg p-2 text-sm"
//                   />

//                 </div>
//               )}

//               <div className="so-solid-divider pt-3">

//                 <div className="flex justify-between mb-2 so-text-ink">

//                   <span>
//                     Subtotal
//                   </span>

//                   <span>
//                     {formatCurrency(
//                       cartTotals.subtotal
//                     )}
//                   </span>

//                 </div>

//                 {isGstEnabled && (
//                   <div className="flex justify-between mb-2 so-text-ink">

//                     <span>
//                       GST
//                     </span>

//                     <span>
//                       {formatCurrency(
//                         cartTotals.taxAmount
//                       )}
//                     </span>

//                   </div>
//                 )}

//                 {cartTotals.discountAmount >
//                   0 && (
//                   <div className="flex justify-between mb-2" style={{ color: "var(--herb-dark)" }}>

//                     <span>
//                       Discount
//                     </span>

//                     <span>
//                       -
//                       {formatCurrency(
//                         cartTotals.discountAmount
//                       )}
//                     </span>

//                   </div>
//                 )}

//                 <div className="flex justify-between font-bold text-lg mb-3 so-text-ink">

//                   <span>
//                     Total
//                   </span>

//                   <span className="so-text-herb">
//                     {formatCurrency(
//                       cartTotals.totalAmount
//                     )}
//                   </span>

//                 </div>

//                 <div className="flex gap-3">

//                   <button
//                     type="button"
//                     onClick={() =>
//                       setShowCartDrawer(false)
//                     }
//                     className="so-invoice-btn-outline flex-1 rounded-lg py-2"
//                   >
//                     Continue
//                   </button>

//                   {renderOrderActionButton({
//                     mobile: true,
//                   })}

//                 </div>

//               </div>

//             </div>

//           </div>
//         )}

//       {/* =====================================================
//           INVOICE
//       ===================================================== */}

//       {showInvoice &&
//         invoiceData && (
//           <div className="fixed inset-0 so-invoice-overlay flex items-center justify-center z-50 px-4">

//             <div className="so-invoice-card shadow-xl w-full max-w-md p-4 md:p-6 overflow-auto max-h-[95vh]">

//               <div
//                 ref={receiptRef}
//                 style={{
//                   background: "white",
//                   paddingBottom: 40,
//                 }}
//               >

//                 {/* BUSINESS */}

//                 <div className="text-center mb-3">

//                   {logoUrl && (
//                     <img
//                       src={logoUrl}
//                       alt="Logo"
//                       className="h-12 mx-auto mb-2 object-contain"
//                     />
//                   )}

//                   <h3 className="so-invoice-title">
//                     {
//                       businessProfile?.businessName ||
//                       "Restaurant POS"
//                     }
//                   </h3>

//                   {businessProfile?.addressLine1 && (
//                     <p className="text-xs so-text-stone">
//                       {
//                         businessProfile.addressLine1
//                       }
//                     </p>
//                   )}

//                   {(businessProfile?.city ||
//                     businessProfile?.state ||
//                     businessProfile?.pinCode) && (
//                     <p className="text-xs so-text-stone">
//                       {[
//                         businessProfile?.city,
//                         businessProfile?.state,
//                         businessProfile?.pinCode,
//                       ]
//                         .filter(Boolean)
//                         .join(", ")}
//                     </p>
//                   )}

//                   {businessProfile?.phone && (
//                     <p className="text-xs so-text-stone">
//                       Tel:{" "}
//                       {
//                         businessProfile.phone
//                       }
//                     </p>
//                   )}

//                   {invoiceData.gstEnabled &&
//                     businessProfile?.gstin && (
//                       <p className="text-xs font-semibold so-text-ink mt-1">
//                         GSTIN:{" "}
//                         {
//                           businessProfile.gstin
//                         }
//                       </p>
//                     )}

//                 </div>

//                 {/* ORDER */}

//                 <div className="flex justify-between text-xs mb-2 so-text-ink">

//                   <span>
//                     Order:{" "}
//                     {
//                       invoiceData.orderNumber
//                     }
//                     {" "}
//                     (
//                     {formatOrderType(
//                       invoiceData.orderType
//                     )}
//                     )
//                   </span>

//                   <span>
//                     {formatDateTime(
//                       invoiceData.createdAt
//                     )}
//                   </span>

//                 </div>

//                 <div className="so-dashed-divider my-2" />

//                 {/* ITEMS */}

//                 <div className="space-y-2 text-sm">

//                   {invoiceData.items.map(
//                     (item) => (
//                       <div
//                         key={item.id}
//                         className="so-solid-divider pb-2 so-text-ink"
//                       >

//                         <div className="flex justify-between">

//                           <span>
//                             {item.name}
//                             {" × "}
//                             {item.quantity}
//                           </span>

//                           <span>
//                             {formatCurrency(
//                               item.lineTotal
//                             )}
//                           </span>

//                         </div>

//                         {invoiceData.gstEnabled &&
//                           item.taxRate > 0 && (
//                             <div className="flex justify-between text-xs so-text-stone mt-1">

//                               <span>
//                                 GST{" "}
//                                 {item.taxRate}%
//                               </span>

//                               <span>
//                                 {formatCurrency(
//                                   item.taxAmount
//                                 )}
//                               </span>

//                             </div>
//                           )}

//                       </div>
//                     )
//                   )}

//                 </div>

//                 <div className="so-dashed-divider my-3" />

//                 {/* SUBTOTAL */}

//                 <div className="flex justify-between text-sm so-text-ink">

//                   <span>
//                     Subtotal
//                   </span>

//                   <span>
//                     {formatCurrency(
//                       invoiceData.subtotal
//                     )}
//                   </span>

//                 </div>

//                 {/* GST */}

//                 {invoiceData.gstEnabled && (
//                   <div className="flex justify-between text-sm mt-1 so-text-ink">

//                     <span>
//                       GST
//                     </span>

//                     <span>
//                       {formatCurrency(
//                         invoiceData.taxAmount
//                       )}
//                     </span>

//                   </div>
//                 )}

//                 {/* DISCOUNT */}

//                 {invoiceData.discountAmount >
//                   0 && (
//                   <div className="flex justify-between text-sm mt-1" style={{ color: "var(--herb-dark)" }}>

//                     <span>
//                       Discount
//                       {invoiceData.discountPercent >
//                         0 &&
//                         ` (${invoiceData.discountPercent}%)`}
//                     </span>

//                     <span>
//                       -
//                       {formatCurrency(
//                         invoiceData.discountAmount
//                       )}
//                     </span>

//                   </div>
//                 )}

//                 {invoiceData.gstEnabled &&
//                   invoiceData.taxInclusive && (
//                     <p className="text-xs so-text-stone mt-2">
//                       GST included in item prices
//                     </p>
//                   )}

//                 {/* TOTAL */}

//                 <div className="flex justify-between font-bold text-lg mt-3 so-text-ink">

//                   <span>
//                     Total
//                   </span>

//                   <span>
//                     {formatCurrency(
//                       invoiceData.totalAmount
//                     )}
//                   </span>

//                 </div>

//                 {/* NOTES */}

//                 {invoiceData.specialInstructions && (
//                   <>
//                     <div className="so-dashed-divider my-3" />

//                     <p className="text-xs so-text-stone">
//                       <strong>
//                         Notes:
//                       </strong>{" "}
//                       {
//                         invoiceData.specialInstructions
//                       }
//                     </p>
//                   </>
//                 )}

//                 <p className="text-center text-sm so-text-stone mt-5">
//                   Thank you for your purchase!
//                 </p>

//               </div>

//               {/* BUTTONS */}

//               <div className="mt-5 flex flex-col sm:flex-row gap-3">

//                 <button
//                   type="button"
//                   onClick={
//                     handleInvoiceDone
//                   }
//                   disabled={
//                     isProcessing
//                   }
//                   className="so-invoice-btn-outline flex-1 rounded-lg py-2"
//                 >
//                   {isProcessing
//                     ? "Sending..."
//                     : "Done"}
//                 </button>

//                 <button
//                   type="button"
//                   onClick={
//                     handlePrint
//                   }
//                   className="so-invoice-btn-print flex-1 rounded-lg py-2"
//                 >
//                   Print
//                 </button>

//                 <button
//                   type="button"
//                   onClick={
//                     handleDownloadPDF
//                   }
//                   className="so-invoice-btn-pdf flex-1 rounded-lg py-2"
//                 >
//                   Download PDF
//                 </button>

//               </div>

//             </div>

//           </div>
//         )}
//     </>
//   );
// }














// // // src/components/POSView.js

// // import React, { useEffect, useRef, useState } from "react";
// // import {
// //   ShoppingCart,
// //   Plus,
// //   Minus,
// //   Trash2,
// // } from "lucide-react";

// // import {
// //   getAvailableMenuItems,
// //   getCategories, 
// //   getBusinessProfile,
// //   getBusinessLogoBlob,
// //   getOrderItems,
// //   addOrderItem,
// //   updateOrderItem,
// //   deleteOrderItem,
// //   updateOrderStatus,
// //   updateOrderType,
// //   updateOrderNotes,
// // } from "../api";

// // import jsPDF from "jspdf";
// // import html2canvas from "html2canvas";

// // const API_BASE_URL =
// //   process.env.REACT_APP_API_URL || "http://localhost:8080";

// // export default function POSView({
// //   table,
// //   order,
// //   onOrderCompleted,
// // }) {
// //   const [menuItems, setMenuItems] = useState([]);
// //   const [categories, setCategories] = useState([]);
// //   const [selectedCategory, setSelectedCategory] =
// //     useState("all");

// //   const [cartItems, setCartItems] = useState([]);
// //   const [addingItemId, setAddingItemId] = useState(null);
// //   const [isProcessing, setIsProcessing] = useState(false);
// //   const [cartDataStale, setCartDataStale] = useState(false);

// //   const [orderType, setOrderType] = useState(
// //     order?.orderType || "DINE_IN"
// //   );

// //   const [specialInstructions, setSpecialInstructions] =
// //     useState(order?.specialInstructions || "");

// //   const [discountPercent, setDiscountPercent] =
// //     useState(Number(order?.discountPercent || 0));

// //   const [discountAmountInput, setDiscountAmountInput] =
// //     useState(Number(order?.discountAmount || 0));

// //   const [businessProfile, setBusinessProfile] =
// //     useState(null);

// //   const [logoUrl, setLogoUrl] = useState(null);

// //   const [showInvoice, setShowInvoice] = useState(false);
// //   const [invoiceData, setInvoiceData] = useState(null);

// //   const [showCartDrawer, setShowCartDrawer] =
// //     useState(false);

// //   const receiptRef = useRef(null);

// //   const [isMobile, setIsMobile] = useState(
// //     typeof window !== "undefined"
// //       ? window.innerWidth < 768
// //       : false
// //   );

// //   // =========================================================
// //   // MOBILE
// //   // =========================================================

// //   useEffect(() => {
// //     const resize = () => {
// //       setIsMobile(window.innerWidth < 768);
// //     };

// //     window.addEventListener("resize", resize);

// //     return () => {
// //       window.removeEventListener("resize", resize);
// //     };
// //   }, []);

// //   // =========================================================
// //   // ORDER STATUS
// //   // =========================================================

// //   const orderStatus = String(
// //     order?.status || ""
// //   ).toUpperCase();

// //   const isEditable =
// //     !order ||
// //     orderStatus === "DRAFT";

// //   const isSentToKitchen =
// //     orderStatus === "PENDING" ||
// //     orderStatus === "IN_PROGRESS";

// //   const isKitchenReady =
// //     orderStatus === "READY";

// //   // =========================================================
// //   // SYNC ORDER
// //   // =========================================================

// //   useEffect(() => {
// //     setOrderType(
// //       order?.orderType || "DINE_IN"
// //     );

// //     setSpecialInstructions(
// //       order?.specialInstructions || ""
// //     );

// //     setDiscountPercent(
// //       Number(order?.discountPercent || 0)
// //     );

// //     setDiscountAmountInput(
// //       Number(order?.discountAmount || 0)
// //     );
// //   }, [order?.id]);

// //   // =========================================================
// //   // LOAD
// //   // =========================================================

// //   useEffect(() => {
// //     loadMenuItems();
// //     loadCategories();
// //     loadBusinessProfile();
// //   }, []);

// //   useEffect(() => {
// //     if (order?.id) {
// //       loadOrderItems();
// //     } else {
// //       setCartItems([]);
// //     }
// //   }, [
// //     order?.id,
// //     order?.status,
// //   ]);

// //   // =========================================================
// //   // LOAD MENU
// //   // =========================================================

// //   const loadMenuItems = async () => {
// //     try {
// //       const data =
// //         await getAvailableMenuItems();

// //       setMenuItems(data || []);
// //     } catch (error) {
// //       console.error(
// //         "Failed to load menu items:",
// //         error
// //       );
// //     }
// //   };

// //   // =========================================================
// //   // LOAD CATEGORIES
// //   // =========================================================

// //   const loadCategories = async () => {
// //     try {
// //       const data =
// //         await getCategories();

// //       setCategories(data || []);
// //     } catch (error) {
// //       console.error(
// //         "Failed to load categories:",
// //         error
// //       );
// //     }
// //   };

// //   // =========================================================
// //   // LOAD BUSINESS PROFILE
// //   // =========================================================

// //   const loadBusinessProfile = async () => {
// //     try {
// //       const data =
// //         await getBusinessProfile();

// //       if (!data) {
// //         return;
// //       }

// //       setBusinessProfile(data);

// //       if (data.hasLogo) {
// //         try {
// //           const response =
// //             await getBusinessLogoBlob();

// //           const url =
// //             URL.createObjectURL(
// //               response.data
// //             );

// //           setLogoUrl(url);
// //         } catch (error) {
// //           console.error(
// //             "Logo loading failed:",
// //             error
// //           );
// //         }
// //       }
// //     } catch (error) {
// //       console.error(
// //         "Business profile error:",
// //         error
// //       );
// //     }
// //   };

// //   // =========================================================
// //   // LOAD ORDER ITEMS
// //   // =========================================================

// //   const loadOrderItems = async () => {
// //     if (!order?.id) {
// //       return;
// //     }

// //     try {
// //       const data =
// //         await getOrderItems(order.id);

// //       setCartItems(data || []);
// //       setCartDataStale(false);
// //     } catch (error) {
// //       console.error(
// //         "Failed to load order items:",
// //         error
// //       );

// //       setCartDataStale(true);
// //     }
// //   };

// //   // =========================================================
// //   // HELPERS
// //   // =========================================================

// //   const toNumber = (value) => {
// //     const number = Number(value);

// //     return Number.isFinite(number)
// //       ? number
// //       : 0;
// //   };

// //   const roundMoney = (value) => {
// //     return Math.round(
// //       (toNumber(value) + Number.EPSILON) * 100
// //     ) / 100;
// //   };

// //   const formatCurrency = (value) => {
// //     return `₹${toNumber(value).toFixed(2)}`;
// //   };

// //   const formatDateTime = (value) => {
// //     if (!value) {
// //       return "";
// //     }

// //     const date = new Date(value);

// //     return (
// //       `${date.toLocaleDateString()} ` +
// //       `${date.toLocaleTimeString()}`
// //     );
// //   };

// //   const formatOrderType = (type) => {
// //     return type === "TAKEAWAY"
// //       ? "🥡 Take Away"
// //       : "🍽 Dine In";
// //   };

// //   // =========================================================
// //   // GST
// //   // =========================================================

// //   const isGstEnabled =
// //     businessProfile?.gstEnabled === true;

// //   const isTaxInclusive =
// //     businessProfile?.taxInclusive === true;

// //   const businessDefaultTaxRate =
// //     toNumber(
// //       businessProfile?.defaultTaxRate
// //     );

// //   /*
// //    * TAX PRIORITY
// //    *
// //    * 1. OrderItem.taxRate
// //    * 2. MenuItem.taxRate
// //    * 3. Business defaultTaxRate
// //    *
// //    * Zero means use business default.
// //    */

// //   const getTaxRate = (item) => {
// //     const orderItemRate =
// //       toNumber(item?.taxRate);

// //     if (orderItemRate > 0) {
// //       return orderItemRate;
// //     }

// //     const menuItemRate =
// //       toNumber(
// //         item?.menuItem?.taxRate
// //       );

// //     if (menuItemRate > 0) {
// //       return menuItemRate;
// //     }

// //     return businessDefaultTaxRate;
// //   };

// //   // =========================================================
// //   // ITEM TAX
// //   // =========================================================

// //   const calculateItemTax = (item) => {
// //     if (!isGstEnabled) {
// //       return 0;
// //     }

// //     const rate =
// //       getTaxRate(item);

// //     if (rate <= 0) {
// //       return 0;
// //     }

// //     const lineTotal =
// //       toNumber(item?.lineTotal);

// //     /*
// //      * If backend already calculated tax,
// //      * use that value.
// //      */
// //     if (
// //       item?.taxAmount !== null &&
// //       item?.taxAmount !== undefined &&
// //       toNumber(item.taxAmount) > 0
// //     ) {
// //       return roundMoney(
// //         item.taxAmount
// //       );
// //     }

// //     /*
// //      * GST INCLUDED
// //      *
// //      * Example:
// //      * Price = ₹525
// //      * GST = 5%
// //      *
// //      * Base = 525 / 1.05 = 500
// //      * GST = 25
// //      */
// //     if (isTaxInclusive) {
// //       const base =
// //         lineTotal /
// //         (1 + rate / 100);

// //       return roundMoney(
// //         lineTotal - base
// //       );
// //     }

// //     /*
// //      * GST EXTRA
// //      *
// //      * Example:
// //      * Price = ₹500
// //      * GST = 5%
// //      * GST = ₹25
// //      * Total = ₹525
// //      */
// //     return roundMoney(
// //       lineTotal * rate / 100
// //     );
// //   };

// //   // =========================================================
// //   // ITEM BASE
// //   // =========================================================

// //   const calculateItemBaseAmount = (item) => {
// //     const lineTotal =
// //       toNumber(item?.lineTotal);

// //     const tax =
// //       calculateItemTax(item);

// //     if (
// //       isGstEnabled &&
// //       isTaxInclusive
// //     ) {
// //       return roundMoney(
// //         lineTotal - tax
// //       );
// //     }

// //     return roundMoney(lineTotal);
// //   };

// //   // =========================================================
// //   // CART TOTALS
// //   // =========================================================

// //   const calculateCartTotals = () => {
// //     let subtotal = 0;
// //     let taxAmount = 0;
// //     let grossTotal = 0;

// //     cartItems.forEach((item) => {
// //       const lineTotal =
// //         toNumber(item?.lineTotal);

// //       const tax =
// //         calculateItemTax(item);

// //       if (
// //         isGstEnabled &&
// //         isTaxInclusive
// //       ) {
// //         /*
// //          * lineTotal already contains GST.
// //          */
// //         subtotal +=
// //           lineTotal - tax;

// //         taxAmount += tax;

// //         grossTotal +=
// //           lineTotal;
// //       } else {
// //         /*
// //          * lineTotal is item price
// //          * before GST.
// //          */
// //         subtotal +=
// //           lineTotal;

// //         taxAmount +=
// //           tax;

// //         grossTotal +=
// //           lineTotal + tax;
// //       }
// //     });

// //     subtotal =
// //       roundMoney(subtotal);

// //     taxAmount =
// //       roundMoney(taxAmount);

// //     grossTotal =
// //       roundMoney(grossTotal);

// //     let percent =
// //       toNumber(discountPercent);

// //     let amount =
// //       toNumber(discountAmountInput);

// //     if (percent < 0) {
// //       percent = 0;
// //     }

// //     if (percent > 100) {
// //       percent = 100;
// //     }

// //     if (amount < 0) {
// //       amount = 0;
// //     }

// //     if (percent > 0) {
// //       amount =
// //         roundMoney(
// //           grossTotal *
// //           percent /
// //           100
// //         );
// //     }

// //     amount =
// //       Math.min(
// //         amount,
// //         grossTotal
// //       );

// //     const totalAmount =
// //       roundMoney(
// //         Math.max(
// //           0,
// //           grossTotal - amount
// //         )
// //       );

// //     return {
// //       subtotal,
// //       taxAmount,
// //       grossTotal,
// //       discountPercent:
// //         roundMoney(percent),
// //       discountAmount:
// //         amount,
// //       totalAmount,
// //     };
// //   };

// //   const cartTotals =
// //     calculateCartTotals();

// //   // =========================================================
// //   // FILTER
// //   // =========================================================

// //   const filteredItems =
// //     selectedCategory === "all"
// //       ? menuItems
// //       : menuItems.filter(
// //           (item) =>
// //             item.category &&
// //             item.category.id ===
// //               selectedCategory
// //         );

// //   // =========================================================
// //   // ADD ITEM
// //   // =========================================================

// //   const addToCart = async (menuItem) => {
// //     if (!order?.id) {
// //       alert(
// //         "No active order found."
// //       );
// //       return;
// //     }

// //     if (!isEditable) {
// //       alert(
// //         "This order has been sent to the kitchen. Editing is disabled."
// //       );
// //       return;
// //     }

// //     if (
// //       addingItemId ===
// //       menuItem.id
// //     ) {
// //       return;
// //     }

// //     setAddingItemId(
// //       menuItem.id
// //     );

// //     try {
// //       await addOrderItem(
// //         order.id,
// //         menuItem.id
// //       );

// //       await loadOrderItems();

// //       if (isMobile) {
// //         setShowCartDrawer(true);
// //       }
// //     } catch (error) {
// //       console.error(error);

// //       alert(
// //         "Unable to add item."
// //       );
// //     } finally {
// //       setAddingItemId(null);
// //     }
// //   };

// //   // =========================================================
// //   // QUANTITY
// //   // =========================================================

// //   const updateQuantity = async (
// //     orderItemId,
// //     change
// //   ) => {
// //     if (!isEditable) {
// //       return;
// //     }

// //     try {
// //       const item =
// //         cartItems.find(
// //           (current) =>
// //             current.id ===
// //             orderItemId
// //         );

// //       if (!item) {
// //         return;
// //       }

// //       const newQuantity =
// //         item.quantity +
// //         change;

// //       if (newQuantity <= 0) {
// //         await deleteOrderItem(
// //           orderItemId
// //         );
// //       } else {
// //         await updateOrderItem(
// //           orderItemId,
// //           newQuantity
// //         );
// //       }

// //       await loadOrderItems();
// //     } catch (error) {
// //       console.error(error);

// //       alert(
// //         "Unable to update quantity."
// //       );
// //     }
// //   };

// //   // =========================================================
// //   // REMOVE
// //   // =========================================================

// //   const removeFromCart = async (
// //     orderItemId
// //   ) => {
// //     if (!isEditable) {
// //       return;
// //     }

// //     try {
// //       await deleteOrderItem(
// //         orderItemId
// //       );

// //       await loadOrderItems();
// //     } catch (error) {
// //       console.error(error);

// //       alert(
// //         "Unable to remove item."
// //       );
// //     }
// //   };

// //   // =========================================================
// //   // ORDER TYPE
// //   // =========================================================

// //   const handleOrderTypeChange =
// //     async (type) => {
// //       if (!isEditable) {
// //         return;
// //       }

// //       setOrderType(type);

// //       try {
// //         await updateOrderType(
// //           order.id,
// //           type
// //         );
// //       } catch (error) {
// //         console.error(error);

// //         alert(
// //           "Unable to save order type."
// //         );
// //       }
// //     };

// //   // =========================================================
// //   // NOTES
// //   // =========================================================

// //   const handleNotesBlur =
// //     async () => {
// //       if (
// //         !order?.id ||
// //         !isEditable
// //       ) {
// //         return;
// //       }

// //       try {
// //         await updateOrderNotes(
// //           order.id,
// //           specialInstructions
// //         );
// //       } catch (error) {
// //         console.error(error);

// //         alert(
// //           "Unable to save notes."
// //         );
// //       }
// //     };

// //   // =========================================================
// //   // COMPLETE ORDER / INVOICE
// //   // =========================================================

// //   const handleCompleteOrder =
// //     () => {
// //       if (!order?.id) {
// //         alert(
// //           "No active order."
// //         );
// //         return;
// //       }

// //       if (
// //         cartItems.length === 0
// //       ) {
// //         alert(
// //           "Cart is empty."
// //         );
// //         return;
// //       }

// //       const invoiceItems =
// //         cartItems.map(
// //           (item) => {
// //             const taxRate =
// //               getTaxRate(item);

// //             const taxAmount =
// //               calculateItemTax(item);

// //             const baseAmount =
// //               calculateItemBaseAmount(
// //                 item
// //               );

// //             return {
// //               id: item.id,
// //               name:
// //                 item.menuItem?.name ||
// //                 "Item",
// //               quantity:
// //                 item.quantity,
// //               price:
// //                 toNumber(item.price),
// //               lineTotal:
// //                 toNumber(
// //                   item.lineTotal
// //                 ),
// //               baseAmount,
// //               taxRate,
// //               taxAmount,
// //               specialInstructions:
// //                 item.specialInstructions ||
// //                 "",
// //             };
// //           }
// //         );

// //       setInvoiceData({
// //         orderNumber:
// //           order.orderNumber,

// //         orderType,

// //         specialInstructions,

// //         createdAt:
// //           order.createdAt ||
// //           new Date().toISOString(),

// //         subtotal:
// //           cartTotals.subtotal,

// //         taxAmount:
// //           cartTotals.taxAmount,

// //         grossTotal:
// //           cartTotals.grossTotal,

// //         discountPercent:
// //           cartTotals.discountPercent,

// //         discountAmount:
// //           cartTotals.discountAmount,

// //         totalAmount:
// //           cartTotals.totalAmount,

// //         gstEnabled:
// //           isGstEnabled,

// //         taxInclusive:
// //           isTaxInclusive,

// //         items:
// //           invoiceItems,
// //       });

// //       setShowInvoice(true);
// //     };

// //   // =========================================================
// //   // DONE / SEND KITCHEN
// //   // =========================================================

// //   const handleInvoiceDone =
// //     async () => {
// //       if (!order?.id) {
// //         setShowInvoice(false);
// //         return;
// //       }

// //       setIsProcessing(true);

// //       try {
// //         await updateOrderType(
// //           order.id,
// //           orderType
// //         );

// //         await updateOrderNotes(
// //           order.id,
// //           specialInstructions
// //         );

// //         /*
// //          * Keep calculated values available
// //          * to the parent UI.
// //          */
// //         order.discountPercent =
// //           cartTotals.discountPercent;

// //         order.discountAmount =
// //           cartTotals.discountAmount;

// //         order.subtotal =
// //           cartTotals.subtotal;

// //         order.taxAmount =
// //           cartTotals.taxAmount;

// //         order.totalAmount =
// //           cartTotals.totalAmount;

// //         /*
// //          * IMPORTANT:
// //          *
// //          * The backend OrderService must calculate and
// //          * persist OrderItem.taxRate, OrderItem.taxAmount,
// //          * Order.taxAmount and Order.totalAmount when
// //          * the order is completed.
// //          *
// //          * Frontend only displays the calculated values.
// //          */
// //         await updateOrderStatus(
// //           order.id,
// //           "PENDING"
// //         );

// //         if (
// //           typeof onOrderCompleted ===
// //           "function"
// //         ) {
// //           onOrderCompleted(order);
// //         }

// //         setCartItems([]);

// //         setSpecialInstructions("");

// //         setOrderType(
// //           "DINE_IN"
// //         );

// //         setDiscountPercent(0);

// //         setDiscountAmountInput(0);

// //         setInvoiceData(null);

// //         setShowInvoice(false);

// //         if (isMobile) {
// //           setShowCartDrawer(false);
// //         }
// //       } catch (error) {
// //         console.error(error);

// //         alert(
// //           "Unable to send order to kitchen."
// //         );
// //       } finally {
// //         setIsProcessing(false);
// //       }
// //     };

// //   // =========================================================
// //   // PRINT
// //   // =========================================================

// //   const handlePrint = () => {
// //     if (!receiptRef.current) {
// //       return;
// //     }

// //     const content =
// //       receiptRef.current.innerHTML;

// //     const printWindow =
// //       window.open(
// //         "",
// //         "_blank",
// //         "width=400,height=700"
// //       );

// //     if (!printWindow) {
// //       alert(
// //         "Popup blocked. Allow popups to print invoice."
// //       );
// //       return;
// //     }

// //     printWindow.document.open();

// //     printWindow.document.write(`
// //       <html>
// //         <head>
// //           <title>GST Invoice</title>

// //           <meta
// //             name="viewport"
// //             content="width=device-width, initial-scale=1"
// //           />

// //           <style>
// //             body {
// //               font-family:
// //                 Arial,
// //                 sans-serif;
// //               padding: 16px;
// //               background: white;
// //               color: #111827;
// //             }

// //             .line {
// //               display: flex;
// //               justify-content: space-between;
// //               margin: 4px 0;
// //             }

// //             .center {
// //               text-align: center;
// //             }

// //             .border-top {
// //               border-top:
// //                 1px dashed #000;
// //               margin-top: 8px;
// //               padding-top: 8px;
// //             }

// //             img {
// //               max-width: 100%;
// //               height: auto;
// //             }
// //           </style>
// //         </head>

// //         <body>
// //           ${content}
// //         </body>
// //       </html>
// //     `);

// //     printWindow.document.close();

// //     printWindow.focus();

// //     setTimeout(() => {
// //       printWindow.print();
// //       printWindow.close();
// //     }, 300);
// //   };

// //   // =========================================================
// //   // PDF
// //   // =========================================================

// //   const handleDownloadPDF =
// //     async () => {
// //       if (
// //         !receiptRef.current ||
// //         !invoiceData
// //       ) {
// //         return;
// //       }

// //       const element =
// //         receiptRef.current;

// //       const originalWidth =
// //         element.style.width;

// //       const originalMaxHeight =
// //         element.style.maxHeight;

// //       try {
// //         element.style.width =
// //           window.innerWidth > 900
// //             ? "700px"
// //             : `${Math.floor(
// //                 window.innerWidth * 0.92
// //               )}px`;

// //         element.style.maxHeight =
// //           "none";

// //         const canvas =
// //           await html2canvas(
// //             element,
// //             {
// //               scale:
// //                 Math.min(
// //                   2,
// //                   window.devicePixelRatio ||
// //                     1.5
// //                 ) * 1.2,

// //               useCORS: true,

// //               scrollX: 0,

// //               scrollY:
// //                 -window.scrollY,

// //               windowWidth:
// //                 element.scrollWidth,

// //               windowHeight:
// //                 element.scrollHeight,
// //             }
// //           );

// //         const imgData =
// //           canvas.toDataURL(
// //             "image/png"
// //           );

// //         const pdf =
// //           new jsPDF(
// //             "p",
// //             "pt",
// //             "a4"
// //           );

// //         const pageWidth =
// //           pdf.internal.pageSize.getWidth();

// //         const pageHeight =
// //           pdf.internal.pageSize.getHeight();

// //         const margin = 20;

// //         const imgWidth =
// //           pageWidth -
// //           margin * 2;

// //         const imgHeight =
// //           (canvas.height *
// //             imgWidth) /
// //           canvas.width;

// //         let heightLeft =
// //           imgHeight;

// //         let position =
// //           margin;

// //         pdf.addImage(
// //           imgData,
// //           "PNG",
// //           margin,
// //           position,
// //           imgWidth,
// //           imgHeight
// //         );

// //         heightLeft -=
// //           pageHeight -
// //           margin * 2;

// //         while (
// //           heightLeft > 0
// //         ) {
// //           pdf.addPage();

// //           position =
// //             margin -
// //             (
// //               imgHeight -
// //               heightLeft
// //             );

// //           pdf.addImage(
// //             imgData,
// //             "PNG",
// //             margin,
// //             position,
// //             imgWidth,
// //             imgHeight
// //           );

// //           heightLeft -=
// //             pageHeight -
// //             margin * 2;
// //         }

// //         pdf.save(
// //           `invoice_${
// //             invoiceData.orderNumber ||
// //             Date.now()
// //           }.pdf`
// //         );
// //       } catch (error) {
// //         console.error(
// //           "PDF generation failed:",
// //           error
// //         );

// //         alert(
// //           "Failed to generate PDF."
// //         );
// //       } finally {
// //         element.style.width =
// //           originalWidth || "";

// //         element.style.maxHeight =
// //           originalMaxHeight || "";
// //       }
// //     };

// //   // =========================================================
// //   // ORDER BUTTON
// //   // =========================================================

// //   const renderOrderActionButton =
// //     ({ mobile = false } = {}) => {
// //       if (!order) {
// //         return null;
// //       }

// //       const baseClass =
// //         mobile
// //           ? "flex-1 rounded-lg py-2 text-sm font-semibold"
// //           : "w-full py-3 rounded-lg font-semibold";

// //       if (
// //         orderStatus === "DRAFT"
// //       ) {
// //         return (
// //           <button
// //             type="button"
// //             onClick={
// //               handleCompleteOrder
// //             }
// //             disabled={
// //               cartItems.length === 0 ||
// //               isProcessing
// //             }
// //             className={`
// //               ${baseClass}
// //               bg-blue-600
// //               hover:bg-blue-700
// //               disabled:bg-slate-300
// //               disabled:cursor-not-allowed
// //               text-white
// //             `}
// //           >
// //             🧾 Complete Order
// //           </button>
// //         );
// //       }

// //       if (
// //         orderStatus === "PENDING" ||
// //         orderStatus === "IN_PROGRESS"
// //       ) {
// //         return (
// //           <button
// //             type="button"
// //             disabled
// //             className={`
// //               ${baseClass}
// //               bg-yellow-500
// //               text-white
// //               cursor-not-allowed
// //             `}
// //           >
// //             ⏳ Waiting For Kitchen
// //           </button>
// //         );
// //       }

// //       if (
// //         orderStatus === "READY"
// //       ) {
// //         return (
// //           <button
// //             type="button"
// //             disabled
// //             className={`
// //               ${baseClass}
// //               bg-emerald-600
// //               text-white
// //               cursor-not-allowed
// //             `}
// //           >
// //             🍽 Kitchen Finished
// //           </button>
// //         );
// //       }

// //       return null;
// //     };

// //   // =========================================================
// //   // RENDER
// //   // =========================================================

// //   return (
// //     <>
// //       <div className="flex flex-col md:flex-row h-full bg-slate-100">

// //         {/* ===================================================
// //             LEFT
// //         =================================================== */}

// //         <div className="flex-1 p-4 md:p-6 overflow-auto">

// //           {/* HEADER */}

// //           <div className="flex items-center justify-between mb-4">

// //             <div>
// //               <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
// //                 Point of Sale
// //               </h2>

// //               <p className="text-sm text-slate-500 mt-1">
// //                 Restaurant POS
// //               </p>
// //             </div>

// //             {isMobile && (
// //               <button
// //                 type="button"
// //                 onClick={() =>
// //                   setShowCartDrawer(true)
// //                 }
// //                 className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg"
// //               >
// //                 <ShoppingCart
// //                   size={18}
// //                 />

// //                 <span>
// //                   Cart ({cartItems.length})
// //                 </span>
// //               </button>
// //             )}
// //           </div>

// //           {/* GST STATUS */}

// //           {businessProfile && (
// //             <div
// //               className={`
// //                 rounded-lg
// //                 px-4
// //                 py-3
// //                 mb-4
// //                 text-sm
// //                 border
// //                 ${
// //                   isGstEnabled &&
// //                   businessDefaultTaxRate > 0
// //                     ? "bg-green-50 border-green-200 text-green-700"
// //                     : "bg-slate-50 border-slate-200 text-slate-500"
// //                 }
// //               `}
// //             >
// //               {isGstEnabled &&
// //               businessDefaultTaxRate > 0 ? (
// //                 <>
// //                   <strong>
// //                     GST Enabled
// //                   </strong>

// //                   {" • "}

// //                   Default GST:{" "}
// //                   <strong>
// //                     {businessDefaultTaxRate}%
// //                   </strong>

// //                   {isTaxInclusive && (
// //                     <>
// //                       {" • "}
// //                       GST included in prices
// //                     </>
// //                   )}
// //                 </>
// //               ) : (
// //                 "GST Disabled"
// //               )}
// //             </div>
// //           )}

// //           {/* STALE */}

// //           {cartDataStale && (
// //             <div className="bg-amber-100 border border-amber-300 text-amber-800 rounded-lg p-3 mb-4 flex justify-between">
// //               <span>
// //                 Cart data may be out of date.
// //               </span>

// //               <button
// //                 type="button"
// //                 onClick={
// //                   loadOrderItems
// //                 }
// //                 className="underline font-semibold"
// //               >
// //                 Retry
// //               </button>
// //             </div>
// //           )}

// //           {/* KITCHEN STATUS */}

// //           {isSentToKitchen && (
// //             <div className="bg-slate-200 border border-slate-300 text-slate-700 rounded-lg p-3 mb-4">
// //               This order has been sent to the kitchen.
// //               Editing is disabled until the kitchen finishes.
// //             </div>
// //           )}

// //           {isKitchenReady && (
// //             <div className="bg-green-100 border border-green-300 text-green-800 rounded-lg p-3 mb-4">
// //               Kitchen has finished this order.
// //               It is ready for billing.
// //             </div>
// //           )}

// //           {/* TABLE */}

// //           {table && (
// //             <div className="bg-green-100 border border-green-300 rounded-xl p-4 mb-5">

// //               <div className="flex justify-between items-center">

// //                 <div>

// //                   <h3 className="text-xl font-bold text-green-800">
// //                     🍽 {table.tableName}
// //                   </h3>

// //                   <p className="text-green-700">
// //                     Capacity: {table.capacity}
// //                   </p>

// //                   <p className="text-green-700">
// //                     Order:{" "}
// //                     {order?.orderNumber}
// //                   </p>

// //                 </div>

// //                 <div className="bg-green-600 text-white px-4 py-2 rounded-full font-semibold">
// //                   {table.status}
// //                 </div>

// //               </div>
// //             </div>
// //           )}

// //           {/* ORDER TYPE */}

// //           {order && (
// //             <div className="mb-5">

// //               <p className="text-sm font-semibold text-slate-700 mb-2">
// //                 Order Type
// //               </p>

// //               <div className="flex gap-3">

// //                 <button
// //                   type="button"
// //                   onClick={() =>
// //                     handleOrderTypeChange(
// //                       "DINE_IN"
// //                     )
// //                   }
// //                   disabled={!isEditable}
// //                   className={`
// //                     px-4
// //                     py-2
// //                     rounded-lg
// //                     border
// //                     ${
// //                       orderType ===
// //                       "DINE_IN"
// //                         ? "bg-blue-600 text-white border-blue-600"
// //                         : "bg-white text-slate-700"
// //                     }
// //                   `}
// //                 >
// //                   🍽 Dine In
// //                 </button>

// //                 <button
// //                   type="button"
// //                   onClick={() =>
// //                     handleOrderTypeChange(
// //                       "TAKEAWAY"
// //                     )
// //                   }
// //                   disabled={!isEditable}
// //                   className={`
// //                     px-4
// //                     py-2
// //                     rounded-lg
// //                     border
// //                     ${
// //                       orderType ===
// //                       "TAKEAWAY"
// //                         ? "bg-blue-600 text-white border-blue-600"
// //                         : "bg-white text-slate-700"
// //                     }
// //                   `}
// //                 >
// //                   🥡 Take Away
// //                 </button>

// //               </div>
// //             </div>
// //           )}

// //           {/* CATEGORIES */}

// //           <div className="flex gap-2 mb-4 overflow-x-auto pb-2">

// //             <button
// //               type="button"
// //               onClick={() =>
// //                 setSelectedCategory(
// //                   "all"
// //                 )
// //               }
// //               className={`
// //                 px-3
// //                 py-2
// //                 rounded-full
// //                 whitespace-nowrap
// //                 text-sm
// //                 ${
// //                   selectedCategory ===
// //                   "all"
// //                     ? "bg-blue-600 text-white"
// //                     : "bg-white text-slate-700"
// //                 }
// //               `}
// //             >
// //               All Items
// //             </button>

// //             {categories.map(
// //               (category) => (
// //                 <button
// //                   type="button"
// //                   key={category.id}
// //                   onClick={() =>
// //                     setSelectedCategory(
// //                       category.id
// //                     )
// //                   }
// //                   className={`
// //                     px-3
// //                     py-2
// //                     rounded-full
// //                     whitespace-nowrap
// //                     text-sm
// //                     ${
// //                       selectedCategory ===
// //                       category.id
// //                         ? "bg-blue-600 text-white"
// //                         : "bg-white text-slate-700"
// //                     }
// //                   `}
// //                 >
// //                   {category.name}
// //                 </button>
// //               )
// //             )}

// //           </div>

// //           {/* MENU */}

// //           <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">

// //             {filteredItems.map(
// //               (item) => {

// //                 const adding =
// //                   addingItemId ===
// //                   item.id;

// //                 const itemTaxRate =
// //                   toNumber(
// //                     item.taxRate
// //                   ) > 0
// //                     ? toNumber(
// //                         item.taxRate
// //                       )
// //                     : businessDefaultTaxRate;

// //                 const displayPrice =
// //                   toNumber(
// //                     item.price
// //                   );

// //                 const gstAmount =
// //                   isGstEnabled &&
// //                   !isTaxInclusive
// //                     ? roundMoney(
// //                         displayPrice *
// //                         itemTaxRate /
// //                         100
// //                       )
// //                     : 0;

// //                 const finalPrice =
// //                   isGstEnabled &&
// //                   !isTaxInclusive
// //                     ? roundMoney(
// //                         displayPrice +
// //                         gstAmount
// //                       )
// //                     : displayPrice;

// //                 return (
// //                   <button
// //                     type="button"
// //                     key={item.id}
// //                     onClick={() =>
// //                       addToCart(item)
// //                     }
// //                     disabled={
// //                       adding ||
// //                       !isEditable
// //                     }
// //                     className={`
// //                       bg-white
// //                       rounded-lg
// //                       shadow
// //                       hover:shadow-lg
// //                       transition
// //                       p-3
// //                       text-left
// //                       ${
// //                         adding ||
// //                         !isEditable
// //                           ? "opacity-50"
// //                           : ""
// //                       }
// //                     `}
// //                   >

// //                     <div className="h-28 md:h-32 bg-slate-200 rounded-lg mb-3 overflow-hidden flex items-center justify-center">

// //                       <img
// //                         src={`${API_BASE_URL}/api/menu-items/${item.id}/image`}
// //                         alt={item.name}
// //                         className="w-full h-full object-cover"
// //                         onError={(event) => {

// //                           if (
// //                             item.imageUrl
// //                           ) {
// //                             event.currentTarget.src =
// //                               item.imageUrl;
// //                           } else {
// //                             event.currentTarget.style.display =
// //                               "none";
// //                           }

// //                         }}
// //                       />

// //                     </div>

// //                     <h3 className="font-semibold text-slate-800">
// //                       {item.name}
// //                     </h3>

// //                     <div className="mt-2">

// //                       <p className="text-lg font-bold text-green-600">
// //                         {formatCurrency(
// //                           finalPrice
// //                         )}
// //                       </p>

// //                       {isGstEnabled &&
// //                         itemTaxRate > 0 && (
// //                           <p className="text-xs text-slate-500 mt-1">
// //                             GST {itemTaxRate}%
// //                             {!isTaxInclusive &&
// //                               ` • ${formatCurrency(
// //                                 gstAmount
// //                               )}`}
// //                             {isTaxInclusive &&
// //                               " included"}
// //                           </p>
// //                         )}

// //                     </div>

// //                   </button>
// //                 );
// //               }
// //             )}

// //           </div>
// //         </div>

// //         {/* ===================================================
// //             DESKTOP CART
// //         =================================================== */}

// //         <aside className="hidden md:flex md:w-96 bg-white shadow-xl p-4 md:p-6 flex-col">

// //           <div className="flex items-center gap-2 mb-4">

// //             <ShoppingCart
// //               size={24}
// //             />

// //             <h3 className="text-2xl font-bold">
// //               Cart
// //             </h3>

// //           </div>

// //           <div className="flex-1 overflow-auto mb-4">

// //             {cartItems.length === 0 ? (
// //               <div className="text-center text-slate-400 mt-12">

// //                 <ShoppingCart
// //                   size={48}
// //                   className="mx-auto mb-2 opacity-50"
// //                 />

// //                 <p>
// //                   Cart is empty
// //                 </p>

// //               </div>
// //             ) : (
// //               <div className="space-y-3">

// //                 {cartItems.map(
// //                   (item) => {

// //                     const taxRate =
// //                       getTaxRate(item);

// //                     const tax =
// //                       calculateItemTax(
// //                         item
// //                       );

// //                     const finalLineTotal =
// //                       isGstEnabled &&
// //                       !isTaxInclusive
// //                         ? roundMoney(
// //                             toNumber(
// //                               item.lineTotal
// //                             ) + tax
// //                           )
// //                         : toNumber(
// //                             item.lineTotal
// //                           );

// //                     return (
// //                       <div
// //                         key={item.id}
// //                         className="bg-slate-50 rounded-lg p-3"
// //                       >

// //                         <div className="flex justify-between items-start mb-2">

// //                           <div>

// //                             <h4 className="font-semibold text-slate-800">
// //                               {
// //                                 item.menuItem
// //                                   ?.name
// //                               }
// //                             </h4>

// //                             {isGstEnabled &&
// //                               taxRate > 0 && (
// //                                 <p className="text-xs text-slate-500">
// //                                   GST{" "}
// //                                   {taxRate}%
// //                                   {" • "}
// //                                   {formatCurrency(
// //                                     tax
// //                                   )}
// //                                 </p>
// //                               )}

// //                           </div>

// //                           <button
// //                             type="button"
// //                             onClick={() =>
// //                               removeFromCart(
// //                                 item.id
// //                               )
// //                             }
// //                             disabled={
// //                               !isEditable
// //                             }
// //                             className="text-red-500 disabled:text-slate-300"
// //                           >
// //                             <Trash2
// //                               size={16}
// //                             />
// //                           </button>

// //                         </div>

// //                         <div className="flex justify-between items-center">

// //                           <div className="flex items-center gap-2">

// //                             <button
// //                               type="button"
// //                               onClick={() =>
// //                                 updateQuantity(
// //                                   item.id,
// //                                   -1
// //                                 )
// //                               }
// //                               disabled={
// //                                 !isEditable
// //                               }
// //                               className="w-8 h-8 bg-white rounded flex items-center justify-center disabled:opacity-40"
// //                             >
// //                               <Minus
// //                                 size={16}
// //                               />
// //                             </button>

// //                             <span className="w-8 text-center font-medium">
// //                               {item.quantity}
// //                             </span>

// //                             <button
// //                               type="button"
// //                               onClick={() =>
// //                                 updateQuantity(
// //                                   item.id,
// //                                   1
// //                                 )
// //                               }
// //                               disabled={
// //                                 !isEditable
// //                               }
// //                               className="w-8 h-8 bg-white rounded flex items-center justify-center disabled:opacity-40"
// //                             >
// //                               <Plus
// //                                 size={16}
// //                               />
// //                             </button>

// //                           </div>

// //                           <span className="font-bold">
// //                             {formatCurrency(
// //                               finalLineTotal
// //                             )}
// //                           </span>

// //                         </div>

// //                       </div>
// //                     );
// //                   }
// //                 )}

// //               </div>
// //             )}

// //           </div>

// //           {/* NOTES */}

// //           {order && (
// //             <div className="mb-4">

// //               <label className="text-sm font-semibold text-slate-700 mb-1 block">
// //                 Special Instructions
// //               </label>

// //               <textarea
// //                 value={
// //                   specialInstructions
// //                 }
// //                 onChange={(event) =>
// //                   setSpecialInstructions(
// //                     event.target.value
// //                   )
// //                 }
// //                 onBlur={
// //                   handleNotesBlur
// //                 }
// //                 disabled={
// //                   !isEditable
// //                 }
// //                 rows={2}
// //                 placeholder="Extra sauce, no onion, less spicy..."
// //                 className="w-full text-sm border border-slate-300 rounded-lg p-2 disabled:bg-slate-100"
// //               />

// //             </div>
// //           )}

// //           {/* TOTALS */}

// //           <div className="border-t pt-4">

// //             <div className="flex justify-between text-sm mb-2">

// //               <span>
// //                 Subtotal
// //               </span>

// //               <span className="font-medium">
// //                 {formatCurrency(
// //                   cartTotals.subtotal
// //                 )}
// //               </span>

// //             </div>

// //             {isGstEnabled && (
// //               <div className="flex justify-between text-sm mb-3">

// //                 <span>
// //                   GST
// //                 </span>

// //                 <span className="font-medium">
// //                   {formatCurrency(
// //                     cartTotals.taxAmount
// //                   )}
// //                 </span>

// //               </div>
// //             )}

// //             {isEditable && (
// //               <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">

// //                 <div className="text-sm font-semibold mb-2">
// //                   Discount
// //                 </div>

// //                 <div className="grid grid-cols-2 gap-2">

// //                   <div>
// //                     <label className="text-xs font-medium text-slate-600 mb-1 block">
// //                       Discount Percentage (%)
// //                     </label>

// //                     <input
// //                       type="number"
// //                       min="0"
// //                       max="100"
// //                       step="0.01"
// //                       value={
// //                         discountPercent
// //                       }
// //                       onChange={(event) => {
// //                         setDiscountPercent(
// //                           Math.min(
// //                             100,
// //                             Math.max(
// //                               0,
// //                               Number(
// //                                 event.target.value
// //                               ) || 0
// //                             )
// //                           )
// //                         );
// //                       }}
// //                       placeholder="Discount %"
// //                       className="w-full border rounded-lg px-2 py-2 text-sm"
// //                     />
// //                   </div>

// //                   <div>
// //                     <label className="text-xs font-medium text-slate-600 mb-1 block">
// //                       Fixed Amount (₹)
// //                     </label>

// //                     <input
// //                       type="number"
// //                       min="0"
// //                       step="0.01"
// //                       value={
// //                         discountPercent > 0
// //                           ? cartTotals.discountAmount
// //                           : discountAmountInput
// //                       }
// //                       onChange={(event) => {
// //                         setDiscountPercent(
// //                           0
// //                         );

// //                         setDiscountAmountInput(
// //                           Math.max(
// //                             0,
// //                             Number(
// //                               event.target.value
// //                             ) || 0
// //                           )
// //                         );
// //                       }}
// //                       placeholder="Amount ₹"
// //                       className="w-full border rounded-lg px-2 py-2 text-sm"
// //                     />
// //                   </div>

// //                 </div>

// //               </div>
// //             )}

// //             {cartTotals.discountAmount >
// //               0 && (
// //               <div className="flex justify-between text-sm mb-3 text-green-700">

// //                 <span>
// //                   Discount
// //                   {cartTotals.discountPercent >
// //                     0 &&
// //                     ` (${cartTotals.discountPercent}%)`}
// //                 </span>

// //                 <span>
// //                   -
// //                   {formatCurrency(
// //                     cartTotals.discountAmount
// //                   )}
// //                 </span>

// //               </div>
// //             )}

// //             <div className="flex justify-between items-center mb-3">

// //               <span className="text-xl font-bold">
// //                 Total
// //               </span>

// //               <span className="text-2xl font-bold text-green-600">
// //                 {formatCurrency(
// //                   cartTotals.totalAmount
// //                 )}
// //               </span>

// //             </div>

// //             {renderOrderActionButton()}

// //           </div>

// //         </aside>

// //         {/* ===================================================
// //             MOBILE BAR
// //         =================================================== */}

// //         {isMobile && (
// //           <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 flex items-center gap-3 z-40">

// //             <div className="flex-1">

// //               <div className="text-sm text-slate-600">
// //                 Total
// //               </div>

// //               <div className="text-lg font-bold text-green-600">
// //                 {formatCurrency(
// //                   cartTotals.totalAmount
// //                 )}
// //               </div>

// //             </div>

// //             <button
// //               type="button"
// //               onClick={() =>
// //                 setShowCartDrawer(true)
// //               }
// //               className="bg-slate-200 px-3 py-2 rounded-lg text-sm"
// //             >
// //               Cart ({
// //                 cartItems.length
// //               })
// //             </button>

// //             {renderOrderActionButton({
// //               mobile: true,
// //             })}

// //           </div>
// //         )}

// //       </div>

// //       {/* =====================================================
// //           MOBILE CART
// //       ===================================================== */}

// //       {isMobile &&
// //         showCartDrawer && (
// //           <div className="fixed inset-0 z-50 flex">

// //             <div
// //               className="absolute inset-0 bg-black/40"
// //               onClick={() =>
// //                 setShowCartDrawer(false)
// //               }
// //             />

// //             <div className="relative w-full max-h-[90vh] bg-white rounded-t-xl shadow-xl mt-auto overflow-auto p-4">

// //               <div className="flex justify-between mb-4">

// //                 <h3 className="text-lg font-semibold">
// //                   Cart
// //                 </h3>

// //                 <button
// //                   type="button"
// //                   onClick={() =>
// //                     setShowCartDrawer(false)
// //                   }
// //                 >
// //                   Close
// //                 </button>

// //               </div>

// //               {!isEditable && (
// //                 <div className="bg-slate-200 border rounded-lg p-2 mb-3 text-sm">
// //                   This order has been sent to the kitchen.
// //                   Editing is disabled.
// //                 </div>
// //               )}

// //               {cartItems.map(
// //                 (item) => {

// //                   const taxRate =
// //                     getTaxRate(item);

// //                   const tax =
// //                     calculateItemTax(
// //                       item
// //                     );

// //                   const finalLineTotal =
// //                     isGstEnabled &&
// //                     !isTaxInclusive
// //                       ? roundMoney(
// //                           toNumber(
// //                             item.lineTotal
// //                           ) + tax
// //                         )
// //                       : toNumber(
// //                           item.lineTotal
// //                         );

// //                   return (
// //                     <div
// //                       key={item.id}
// //                       className="bg-slate-50 rounded-lg p-3 mb-3"
// //                     >

// //                       <div className="flex justify-between">

// //                         <div>

// //                           <h4 className="font-semibold">
// //                             {
// //                               item.menuItem
// //                                 ?.name
// //                             }
// //                           </h4>

// //                           {isGstEnabled &&
// //                             taxRate > 0 && (
// //                               <p className="text-xs text-slate-500">
// //                                 GST{" "}
// //                                 {taxRate}%
// //                                 {" • "}
// //                                 {formatCurrency(
// //                                   tax
// //                                 )}
// //                               </p>
// //                             )}

// //                         </div>

// //                         <button
// //                           type="button"
// //                           onClick={() =>
// //                             removeFromCart(
// //                               item.id
// //                             )
// //                           }
// //                           disabled={
// //                             !isEditable
// //                           }
// //                           className="text-red-500"
// //                         >
// //                           <Trash2
// //                             size={16}
// //                           />
// //                         </button>

// //                       </div>

// //                       <div className="flex justify-between items-center mt-3">

// //                         <div className="flex items-center gap-2">

// //                           <button
// //                             type="button"
// //                             onClick={() =>
// //                               updateQuantity(
// //                                 item.id,
// //                                 -1
// //                               )
// //                             }
// //                             disabled={
// //                               !isEditable
// //                             }
// //                             className="w-8 h-8 bg-white rounded"
// //                           >
// //                             <Minus
// //                               size={16}
// //                               className="mx-auto"
// //                             />
// //                           </button>

// //                           <span>
// //                             {item.quantity}
// //                           </span>

// //                           <button
// //                             type="button"
// //                             onClick={() =>
// //                               updateQuantity(
// //                                 item.id,
// //                                 1
// //                               )
// //                             }
// //                             disabled={
// //                               !isEditable
// //                             }
// //                             className="w-8 h-8 bg-white rounded"
// //                           >
// //                             <Plus
// //                               size={16}
// //                               className="mx-auto"
// //                             />
// //                           </button>

// //                         </div>

// //                         <strong>
// //                           {formatCurrency(
// //                             finalLineTotal
// //                           )}
// //                         </strong>

// //                       </div>

// //                     </div>
// //                   );
// //                 }
// //               )}

// //               {/* MOBILE NOTES */}

// //               {order && (
// //                 <div className="mb-4">

// //                   <label className="text-sm font-semibold block mb-1">
// //                     Special Instructions
// //                   </label>

// //                   <textarea
// //                     value={
// //                       specialInstructions
// //                     }
// //                     onChange={(event) =>
// //                       setSpecialInstructions(
// //                         event.target.value
// //                       )
// //                     }
// //                     onBlur={
// //                       handleNotesBlur
// //                     }
// //                     disabled={
// //                       !isEditable
// //                     }
// //                     rows={2}
// //                     placeholder="Extra sauce, no onion..."
// //                     className="w-full border rounded-lg p-2 text-sm"
// //                   />

// //                 </div>
// //               )}

// //               <div className="border-t pt-3">

// //                 <div className="flex justify-between mb-2">

// //                   <span>
// //                     Subtotal
// //                   </span>

// //                   <span>
// //                     {formatCurrency(
// //                       cartTotals.subtotal
// //                     )}
// //                   </span>

// //                 </div>

// //                 {isGstEnabled && (
// //                   <div className="flex justify-between mb-2">

// //                     <span>
// //                       GST
// //                     </span>

// //                     <span>
// //                       {formatCurrency(
// //                         cartTotals.taxAmount
// //                       )}
// //                     </span>

// //                   </div>
// //                 )}

// //                 {cartTotals.discountAmount >
// //                   0 && (
// //                   <div className="flex justify-between mb-2 text-green-700">

// //                     <span>
// //                       Discount
// //                     </span>

// //                     <span>
// //                       -
// //                       {formatCurrency(
// //                         cartTotals.discountAmount
// //                       )}
// //                     </span>

// //                   </div>
// //                 )}

// //                 <div className="flex justify-between font-bold text-lg mb-3">

// //                   <span>
// //                     Total
// //                   </span>

// //                   <span className="text-green-600">
// //                     {formatCurrency(
// //                       cartTotals.totalAmount
// //                     )}
// //                   </span>

// //                 </div>

// //                 <div className="flex gap-3">

// //                   <button
// //                     type="button"
// //                     onClick={() =>
// //                       setShowCartDrawer(false)
// //                     }
// //                     className="flex-1 border rounded-lg py-2"
// //                   >
// //                     Continue
// //                   </button>

// //                   {renderOrderActionButton({
// //                     mobile: true,
// //                   })}

// //                 </div>

// //               </div>

// //             </div>

// //           </div>
// //         )}

// //       {/* =====================================================
// //           INVOICE
// //       ===================================================== */}

// //       {showInvoice &&
// //         invoiceData && (
// //           <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">

// //             <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-4 md:p-6 overflow-auto max-h-[95vh]">

// //               <div
// //                 ref={receiptRef}
// //                 style={{
// //                   background: "white",
// //                   paddingBottom: 40,
// //                 }}
// //               >

// //                 {/* BUSINESS */}

// //                 <div className="text-center mb-3">

// //                   {logoUrl && (
// //                     <img
// //                       src={logoUrl}
// //                       alt="Logo"
// //                       className="h-12 mx-auto mb-2 object-contain"
// //                     />
// //                   )}

// //                   <h3 className="text-xl font-bold">
// //                     {
// //                       businessProfile?.businessName ||
// //                       "Restaurant POS"
// //                     }
// //                   </h3>

// //                   {businessProfile?.addressLine1 && (
// //                     <p className="text-xs text-slate-500">
// //                       {
// //                         businessProfile.addressLine1
// //                       }
// //                     </p>
// //                   )}

// //                   {(businessProfile?.city ||
// //                     businessProfile?.state ||
// //                     businessProfile?.pinCode) && (
// //                     <p className="text-xs text-slate-500">
// //                       {[
// //                         businessProfile?.city,
// //                         businessProfile?.state,
// //                         businessProfile?.pinCode,
// //                       ]
// //                         .filter(Boolean)
// //                         .join(", ")}
// //                     </p>
// //                   )}

// //                   {businessProfile?.phone && (
// //                     <p className="text-xs text-slate-500">
// //                       Tel:{" "}
// //                       {
// //                         businessProfile.phone
// //                       }
// //                     </p>
// //                   )}

// //                   {invoiceData.gstEnabled &&
// //                     businessProfile?.gstin && (
// //                       <p className="text-xs font-semibold mt-1">
// //                         GSTIN:{" "}
// //                         {
// //                           businessProfile.gstin
// //                         }
// //                       </p>
// //                     )}

// //                 </div>

// //                 {/* ORDER */}

// //                 <div className="flex justify-between text-xs mb-2">

// //                   <span>
// //                     Order:{" "}
// //                     {
// //                       invoiceData.orderNumber
// //                     }
// //                     {" "}
// //                     (
// //                     {formatOrderType(
// //                       invoiceData.orderType
// //                     )}
// //                     )
// //                   </span>

// //                   <span>
// //                     {formatDateTime(
// //                       invoiceData.createdAt
// //                     )}
// //                   </span>

// //                 </div>

// //                 <div className="border-t border-dashed my-2" />

// //                 {/* ITEMS */}

// //                 <div className="space-y-2 text-sm">

// //                   {invoiceData.items.map(
// //                     (item) => (
// //                       <div
// //                         key={item.id}
// //                         className="border-b pb-2"
// //                       >

// //                         <div className="flex justify-between">

// //                           <span>
// //                             {item.name}
// //                             {" × "}
// //                             {item.quantity}
// //                           </span>

// //                           <span>
// //                             {formatCurrency(
// //                               item.lineTotal
// //                             )}
// //                           </span>

// //                         </div>

// //                         {invoiceData.gstEnabled &&
// //                           item.taxRate > 0 && (
// //                             <div className="flex justify-between text-xs text-slate-500 mt-1">

// //                               <span>
// //                                 GST{" "}
// //                                 {item.taxRate}%
// //                               </span>

// //                               <span>
// //                                 {formatCurrency(
// //                                   item.taxAmount
// //                                 )}
// //                               </span>

// //                             </div>
// //                           )}

// //                       </div>
// //                     )
// //                   )}

// //                 </div>

// //                 <div className="border-t border-dashed my-3" />

// //                 {/* SUBTOTAL */}

// //                 <div className="flex justify-between text-sm">

// //                   <span>
// //                     Subtotal
// //                   </span>

// //                   <span>
// //                     {formatCurrency(
// //                       invoiceData.subtotal
// //                     )}
// //                   </span>

// //                 </div>

// //                 {/* GST */}

// //                 {invoiceData.gstEnabled && (
// //                   <div className="flex justify-between text-sm mt-1">

// //                     <span>
// //                       GST
// //                     </span>

// //                     <span>
// //                       {formatCurrency(
// //                         invoiceData.taxAmount
// //                       )}
// //                     </span>

// //                   </div>
// //                 )}

// //                 {/* DISCOUNT */}

// //                 {invoiceData.discountAmount >
// //                   0 && (
// //                   <div className="flex justify-between text-sm mt-1 text-green-700">

// //                     <span>
// //                       Discount
// //                       {invoiceData.discountPercent >
// //                         0 &&
// //                         ` (${invoiceData.discountPercent}%)`}
// //                     </span>

// //                     <span>
// //                       -
// //                       {formatCurrency(
// //                         invoiceData.discountAmount
// //                       )}
// //                     </span>

// //                   </div>
// //                 )}

// //                 {invoiceData.gstEnabled &&
// //                   invoiceData.taxInclusive && (
// //                     <p className="text-xs text-slate-500 mt-2">
// //                       GST included in item prices
// //                     </p>
// //                   )}

// //                 {/* TOTAL */}

// //                 <div className="flex justify-between font-bold text-lg mt-3">

// //                   <span>
// //                     Total
// //                   </span>

// //                   <span>
// //                     {formatCurrency(
// //                       invoiceData.totalAmount
// //                     )}
// //                   </span>

// //                 </div>

// //                 {/* NOTES */}

// //                 {invoiceData.specialInstructions && (
// //                   <>
// //                     <div className="border-t border-dashed my-3" />

// //                     <p className="text-xs text-slate-600">
// //                       <strong>
// //                         Notes:
// //                       </strong>{" "}
// //                       {
// //                         invoiceData.specialInstructions
// //                       }
// //                     </p>
// //                   </>
// //                 )}

// //                 <p className="text-center text-sm text-slate-500 mt-5">
// //                   Thank you for your purchase!
// //                 </p>

// //               </div>

// //               {/* BUTTONS */}

// //               <div className="mt-5 flex flex-col sm:flex-row gap-3">

// //                 <button
// //                   type="button"
// //                   onClick={
// //                     handleInvoiceDone
// //                   }
// //                   disabled={
// //                     isProcessing
// //                   }
// //                   className="flex-1 border border-slate-300 rounded-lg py-2 disabled:opacity-50"
// //                 >
// //                   {isProcessing
// //                     ? "Sending..."
// //                     : "Done"}
// //                 </button>

// //                 <button
// //                   type="button"
// //                   onClick={
// //                     handlePrint
// //                   }
// //                   className="flex-1 bg-emerald-600 text-white rounded-lg py-2 hover:bg-emerald-700"
// //                 >
// //                   Print
// //                 </button>

// //                 <button
// //                   type="button"
// //                   onClick={
// //                     handleDownloadPDF
// //                   }
// //                   className="flex-1 bg-blue-600 text-white rounded-lg py-2 hover:bg-blue-700"
// //                 >
// //                   Download PDF
// //                 </button>

// //               </div>

// //             </div>

// //           </div>
// //         )}
// //     </>
// //   );
// // }
